import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"

// スマホで取得するトピック数。サーバー側(TopicsController#graph_data)は 10..100 に丸める。
// 選んだキーワードの「その先」（2つ先）をどれくらい薄く出すか。
// 0 にすると辿れる先があること自体が見えなくなり、1 に近いと絞った意味が消える。
const SECOND_DEGREE_OPACITY = 0.28

// 長押しで複数選択へ入るまでの時間。短いと「掴んで動かす」と喧嘩し、
// 長いと押しているのに何も起きない時間ができる
const LONG_PRESS_MS = 450

const MOBILE_TOPIC_LIMIT = 120

// SP 判定のしきい値。Tailwind の sm: と同じ 640px に揃えてある。
const MOBILE_BREAKPOINT = 640

// 「世界」(ノードが置かれる矩形)をカードの総面積の何倍にするか の逆数。
// 小さくすると世界が広がって疎に、大きくすると狭まって密になる。
// 0.24 = カードが世界の面積の 24% を占める。390x844 で1画面に十数枚見え、
// 端まで振り切っても空白だけの画面にならない値（0.18 だと世界の端が寂しかった）。
const WORLD_FILL_RATIO = 0.24

// 世界の外側に許すパン量(px)。0 にすると端のカードが画面端に貼り付いて窮屈に見える。
const WORLD_PAN_PADDING = 40

// 線を間引くときに、画面の外どれだけまでを「見えている」とみなすか(px)
const VIEW_MARGIN = 120

// 差を「世界の幅の半分」に収める。トーラスでの最短方向を返す
function wrapDelta(delta, size) {
  const half = size / 2
  return ((delta + half) % size + size) % size - half
}

// 探索の経路として残す区間の上限（ノードは +1 個）。
// 超えたら古い方から落とす。画面が道で埋まらないことが上限の根拠。
const TRAIL_MAX_SEGMENTS = 8

export default class extends Controller {
  static values = { url: String, days: { type: Number, default: 1 }, hours: { type: Number, default: 0 }, categoryColors: Object, categoryNames: Object }

  connect() {
    this.selectedNode = null
    this.connectedNodes = new Set()
    this.secondDegreeNodes = new Set()
    this.multiSelect = false
    this.multiIds = new Set()
    this.zoomLevel = 1
    this.clearTrail()        // 経路 = トピック id の並び（末尾が今いる所）
    this.loadGraph()

    // Setup event listeners
    document.getElementById("close-panel")?.addEventListener("click", () => this.closePanel())
    document.getElementById("quality-good")?.addEventListener("click", () => this.voteQuality("good"))
    document.getElementById("quality-bad")?.addEventListener("click", () => this.voteQuality("bad"))
    this.sheetExpanded = false
    document.getElementById("sheet-handle")?.addEventListener("click", () => this.setSheetExpanded(!this.sheetExpanded))
    // 絞り込みを解く導線。パネルを閉じただけでは解けないので、ここが唯一の出口になる
    document.getElementById("filter-chip-clear")?.addEventListener("click", () =>
      this.multiSelect ? this.exitMultiSelect() : this.clearSelection())
    document.getElementById("filter-chip-name")?.addEventListener("click", () => {
      if (this.multiSelect) this.showIntersectionPanel()
      else if (this.selectedNode) this.showArticlePanel(this.selectedNode)
    })
    document.getElementById("dismiss-instructions")?.addEventListener("click", () => {
      document.getElementById("instructions")?.classList.add("opacity-0", "pointer-events-none")
    })
    document.getElementById("discover-btn")?.addEventListener("click", () => this.discoverRandom())
    document.getElementById("trail-back")?.addEventListener("click", () => this.trailBack())
    document.getElementById("trail-clear")?.addEventListener("click", () => this.clearTrail())

    // Search functionality
    this.setupSearch()

    // Resize handler
    this.resizeHandler = this.debounce(() => {
      if (this.graphData) this.renderGraph(this.graphData)
    }, 250)
    window.addEventListener("resize", this.resizeHandler)

    // Keyboard shortcuts
    document.addEventListener("keydown", (e) => this.handleKeyboard(e))

    // Check URL for shared topic
    this.checkSharedTopic()

    // Minimap toggle
    document.getElementById("toggle-minimap")?.addEventListener("click", () => {
      const map = document.getElementById("minimap")
      map?.classList.toggle("hidden")
    })


    // Zoom buttons
    document.getElementById("zoom-in-btn")?.addEventListener("click", () => this.zoomIn())
    document.getElementById("zoom-out-btn")?.addEventListener("click", () => this.zoomOut())

    // SP: swipe-down to close article panel
    this.setupPanelSwipe()

    // Refresh button
    document.getElementById("refresh-news-btn")?.addEventListener("click", () => this.refreshNews())

    // Auto-refresh every 5 minutes
    // 読み手のブラウザからは取り込みを起こさない。返ってくる latest_at の変化だけを見る
    this.lastLatestAt = null
    this.autoRefreshInterval = setInterval(() => {
      this.silentRefresh()
    }, 5 * 60 * 1000)

    // Favorites from localStorage
    this.favorites = JSON.parse(localStorage.getItem("favoriteTopics") || "[]")
    this.showOnlyFavorites = false

    // Favorites filter button
    // お気に入りボタンは PC のヘッダと SP の下段の2箇所に出るので、id ではなく class で拾う
    document.querySelectorAll(".favorites-filter-btn")
      .forEach(btn => btn.addEventListener("click", () => this.toggleFavoritesFilter()))

    // Shortcuts modal
    document.getElementById("close-shortcuts")?.addEventListener("click", () => this.toggleShortcutsModal())
    document.getElementById("shortcuts-modal")?.addEventListener("click", (e) => {
      if (e.target.id === "shortcuts-modal") this.toggleShortcutsModal()
    })

    // Fullscreen
    document.getElementById("fullscreen-btn")?.addEventListener("click", () => this.toggleFullscreen())
    document.addEventListener("fullscreenchange", () => this.updateFullscreenUI())

    // Trending topics click
    document.querySelectorAll(".trending-topic").forEach(btn => {
      btn.addEventListener("click", () => {
        const topicId = parseInt(btn.dataset.topicId)
        if (this.graphData?.nodes) {
          const topic = this.graphData.nodes.find(n => n.id === topicId)
          if (topic) {
            this.selectNode(topic, this.node, this.link, this.colorScale, this.cardWidth, this.cardHeight)
            this.panToNode(topic)
          }
        }
      })
    })

    // Stats tabs
    this.setupStatsTabs()

    // Playback functionality
    this.setupPlayback()

    // Media filter functionality
    this.setupMediaFilter()

    // Mobile UI functionality
    this.setupMobileUI()

    // Legend toggle
    this.setupLegendToggle()

    // Trending topics hover highlight
    this.setupTrendingHover()
  }

  catHex(cat) {
    return this.categoryColorsValue[cat] || "#9ca3af"
  }

  catName(cat) {
    return this.categoryNamesValue[cat] || cat
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
    if (this.simulation) this.simulation.stop()
    if (this.autoRefreshInterval) clearInterval(this.autoRefreshInterval)
    this.tooltip?.remove()
  }

  debounce(func, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  checkSharedTopic() {
    const params = new URLSearchParams(window.location.search)
    this.sharedTopicId = params.get("topic") ? parseInt(params.get("topic")) : null
  }

  setupSearch() {
    const searchInput = document.getElementById("topic-search")
    const resultsContainer = document.getElementById("search-results")
    if (!searchInput || !resultsContainer) return

    let debounceTimer

    searchInput.addEventListener("input", () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        const query = searchInput.value.trim().toLowerCase()
        if (query.length < 1) {
          resultsContainer.classList.add("hidden")
          this.highlightSearchResults([])
          return
        }
        this.performSearch(query, resultsContainer)
      }, 150)
    })

    searchInput.addEventListener("focus", () => {
      if (searchInput.value.trim().length >= 1 && this.graphData?.nodes) {
        this.performSearch(searchInput.value.trim().toLowerCase(), resultsContainer)
      }
    })

    // Close on click outside
    document.addEventListener("click", (e) => {
      if (!searchInput.contains(e.target) && !resultsContainer.contains(e.target)) {
        resultsContainer.classList.add("hidden")
      }
    })

    // Keyboard navigation
    searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        searchInput.blur()
        resultsContainer.classList.add("hidden")
        this.highlightSearchResults([])
      } else if (e.key === "Enter") {
        const firstResult = resultsContainer.querySelector(".search-result-item")
        if (firstResult) firstResult.click()
      }
    })
  }

  performSearch(query, container) {
    if (!this.graphData?.nodes) return

    const results = this.graphData.nodes.filter(n =>
      n.name.toLowerCase().includes(query)
    ).slice(0, 8)

    if (results.length === 0) {
      container.innerHTML = '<div class="p-3 text-sm text-gray-500">見つかりませんでした</div>'
      container.classList.remove("hidden")
      this.highlightSearchResults([])
      return
    }

    container.innerHTML = results.map(n => {
      return `
        <button class="search-result-item w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between gap-2 transition" data-topic-id="${n.id}">
          <span class="text-sm text-gray-900 dark:text-white truncate">${this.highlightMatch(n.name, query)}</span>
          <span class="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"><span class="w-1.5 h-1.5 rounded-full flex-shrink-0" style="background-color: ${this.catHex(n.category)}"></span>${n.size}件</span>
        </button>
      `
    }).join("")

    container.classList.remove("hidden")

    // Highlight matching nodes in graph
    this.highlightSearchResults(results.map(r => r.id))

    // Add click handlers
    container.querySelectorAll(".search-result-item").forEach(btn => {
      btn.addEventListener("click", () => {
        const topic = this.graphData.nodes.find(n => n.id === parseInt(btn.dataset.topicId))
        if (topic) {
          this.selectNode(topic, this.node, this.link, this.colorScale, this.cardWidth, this.cardHeight)
          this.panToNode(topic)
          document.getElementById("topic-search").value = ""
          container.classList.add("hidden")
          this.highlightSearchResults([])
        }
      })
    })
  }

  highlightMatch(text, query) {
    const idx = text.toLowerCase().indexOf(query)
    if (idx === -1) return text
    return text.slice(0, idx) + '<mark class="bg-yellow-200 dark:bg-yellow-700 rounded px-0.5">' + text.slice(idx, idx + query.length) + '</mark>' + text.slice(idx + query.length)
  }

  highlightSearchResults(matchingIds) {
    if (!this.node) return

    if (matchingIds.length === 0) {
      // Reset all nodes
      this.node.transition().duration(200).attr("opacity", 1)
      this.node.select(".card-bg").transition().duration(200)
        .attr("stroke-width", 2)
    } else {
      // Dim non-matching, highlight matching
      this.node.transition().duration(200)
        .attr("opacity", n => matchingIds.includes(n.id) ? 1 : 0.2)
      this.node.select(".card-bg").transition().duration(200)
        .attr("stroke-width", n => matchingIds.includes(n.id) ? 3 : 2)
    }
  }

  panToNode(d) {
    const svg = d3.select("#newspaper-graph")
    // 倍率を掛けずに translate していたため、拡大中は狙ったノードが中心に来ていなかった
    const k = this.zoomLevel || 1
    const x = this.svgWidth / 2 - d.x * k
    const y = this.svgHeight / 2 - d.y * k
    svg.transition().duration(500)
      .call(this.zoom.transform, d3.zoomIdentity.translate(x, y).scale(k))
  }

  initMinimap(data) {
    const minimapContainer = document.getElementById("minimap-container")
    const minimapSvg = document.getElementById("minimap")
    if (!minimapContainer || !minimapSvg || data.nodes.length < 5) return

    minimapContainer.classList.remove("hidden")

    const miniWidth = 128
    const miniHeight = 96
    const padding = 8

    // Calculate bounds
    const xExtent = d3.extent(data.nodes, d => d.x) || [0, this.svgWidth]
    const yExtent = d3.extent(data.nodes, d => d.y) || [0, this.svgHeight]

    this.minimapScaleX = d3.scaleLinear()
      .domain([xExtent[0] - 50, xExtent[1] + 50])
      .range([padding, miniWidth - padding])

    this.minimapScaleY = d3.scaleLinear()
      .domain([yExtent[0] - 50, yExtent[1] + 50])
      .range([padding, miniHeight - padding])

    const miniSvg = d3.select("#minimap")
    miniSvg.selectAll("*").remove()
    miniSvg.attr("viewBox", `0 0 ${miniWidth} ${miniHeight}`)

    // Draw nodes as dots
    miniSvg.selectAll("circle")
      .data(data.nodes)
      .enter()
      .append("circle")
      .attr("cx", d => this.minimapScaleX(d.x))
      .attr("cy", d => this.minimapScaleY(d.y))
      .attr("r", d => Math.max(2, Math.min(4, d.size / 3)))
      .attr("fill", d => this.colorScale(d.category))
      .attr("opacity", 0.7)

    // Viewport indicator
    this.minimapViewport = miniSvg.append("rect")
      .attr("class", "viewport-indicator")
      .attr("fill", "none")
      .attr("stroke", "#3b82f6")
      .attr("stroke-width", 1.5)
      .attr("rx", 2)

    this.updateMinimapViewport(d3.zoomIdentity)

    // Click to navigate
    miniSvg.on("click", (event) => {
      const [mx, my] = d3.pointer(event)
      const targetX = this.minimapScaleX.invert(mx)
      const targetY = this.minimapScaleY.invert(my)
      const svg = d3.select("#newspaper-graph")
      const k = this.zoomLevel || 1
      const x = this.svgWidth / 2 - targetX * k
      const y = this.svgHeight / 2 - targetY * k
      svg.transition().duration(300)
        .call(this.zoom.transform, d3.zoomIdentity.translate(x, y).scale(k))
    })
  }

  updateMinimapViewport(transform) {
    if (!this.minimapViewport || !this.minimapScaleX) return

    const scale = transform.k
    const viewWidth = this.svgWidth / scale
    const viewHeight = this.svgHeight / scale
    const viewX = -transform.x / scale
    const viewY = -transform.y / scale

    this.minimapViewport
      .attr("x", this.minimapScaleX(viewX))
      .attr("y", this.minimapScaleY(viewY))
      .attr("width", Math.abs(this.minimapScaleX(viewX + viewWidth) - this.minimapScaleX(viewX)))
      .attr("height", Math.abs(this.minimapScaleY(viewY + viewHeight) - this.minimapScaleY(viewY)))
  }

  updateMinimapNodes() {
    if (!this.graphData?.nodes || !this.minimapScaleX) return

    d3.select("#minimap").selectAll("circle")
      .data(this.graphData.nodes)
      .attr("cx", d => this.minimapScaleX(d.x))
      .attr("cy", d => this.minimapScaleY(d.y))
  }

  handleKeyboard(e) {
    // Ignore if typing in input (except Escape)
    if (e.target.tagName === "INPUT" && e.key !== "Escape") return

    if (e.key === "Escape") {
      if (e.target.tagName === "INPUT") {
        e.target.blur()
        document.getElementById("search-results")?.classList.add("hidden")
        this.highlightSearchResults([])
      } else {
        this.clearSelection()
      }
    } else if (e.key === "/" && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      document.getElementById("topic-search")?.focus()
    } else if (e.key === "r" && !e.ctrlKey && !e.metaKey) {
      const panel = document.getElementById("article-panel")
      if (!this.isPanelOpen()) {
        this.discoverRandom()
      }
    } else if (e.key === "ArrowRight" || e.key === "ArrowLeft" || e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault()
      this.navigateToConnected(e.key)
    } else if (e.key === "+" || e.key === "=") {
      this.zoomIn()
    } else if (e.key === "-") {
      this.zoomOut()
    } else if (e.key === "0") {
      this.resetZoom()
    } else if (e.key === "f" && this.selectedNode) {
      this.toggleFocusMode()
    } else if (e.key === "?") {
      this.toggleShortcutsModal()
    }
  }

  toggleShortcutsModal() {
    const modal = document.getElementById("shortcuts-modal")
    if (!modal) return
    modal.classList.toggle("hidden")
  }

  navigateToConnected(direction) {
    if (!this.selectedNode || this.connectedNodes.size === 0) return

    const connected = this.graphData.nodes.filter(n => this.connectedNodes.has(n.id))
    if (connected.length === 0) return

    // Sort by position based on direction
    let sorted
    const current = this.selectedNode
    switch (direction) {
      case "ArrowRight":
        sorted = connected.filter(n => n.x > current.x).sort((a, b) => a.x - b.x)
        break
      case "ArrowLeft":
        sorted = connected.filter(n => n.x < current.x).sort((a, b) => b.x - a.x)
        break
      case "ArrowUp":
        sorted = connected.filter(n => n.y < current.y).sort((a, b) => b.y - a.y)
        break
      case "ArrowDown":
        sorted = connected.filter(n => n.y > current.y).sort((a, b) => a.y - b.y)
        break
    }

    if (sorted && sorted.length > 0) {
      this.selectNode(sorted[0], this.node, this.link, this.colorScale, this.cardWidth, this.cardHeight)
    }
  }

  async loadGraph() {
    try {
      // モバイルは表示トピック数を絞る。ただし 20 は絞りすぎで、
      // 「スクロールしてもすぐ何も無くなる」原因になっていた（PC の既定は 50）。
      let url = this.urlValue
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT
      if (isMobile && !url.includes('limit=')) {
        const separator = url.includes('?') ? '&' : '?'
        url = `${url}${separator}limit=${MOBILE_TOPIC_LIMIT}`
      }
      
      const response = await fetch(url)
      if (!response.ok) throw new Error(`グラフ取得エラー: ${response.status}`)
      this.graphData = await response.json()
      this.loggedIn = !!this.graphData.logged_in
      // 端末に貯めていた★をアカウントへ移す。移した直後は印が古いので取り直す
      if (await this.migrateLocalFavorites()) return this.loadGraph()
      this.syncMarksFromServer()
      // 道は今表示されているノードの上にしか引けない。データを取り直したら経路も捨てる
      // （残すと「更新」の後に押しても何も起きない経路が残る）
      this.clearTrail()
      this.hideFilterChip()
      this.renderGraph(this.graphData)

      // Render stats panels
      if (this.graphData.stats) {
        this.renderStats(this.graphData.stats)
      }

      // Auto-select shared topic if present
      if (this.sharedTopicId) {
        setTimeout(() => {
          const topic = this.graphData.nodes.find(n => n.id === this.sharedTopicId)
          if (topic) {
            this.selectNode(topic, this.node, this.link, this.colorScale, this.cardWidth, this.cardHeight)
          }
        }, 500)
      }
    } catch (error) {
      console.error("Failed to load graph:", error)
      document.getElementById("loading").innerHTML =
        '<div class="text-red-500">読み込みに失敗しました</div>'
    }
  }

  setupStatsTabs() {
    document.querySelectorAll(".stats-tab").forEach(tab => {
      tab.addEventListener("click", () => {
        const tabName = tab.dataset.statsTab

        // Update tab styles
        document.querySelectorAll(".stats-tab").forEach(t => {
          t.classList.remove("text-gray-700", "dark:text-gray-300", "border-blue-500")
          t.classList.add("text-gray-400", "dark:text-gray-500", "border-transparent")
        })
        tab.classList.remove("text-gray-400", "dark:text-gray-500", "border-transparent")
        tab.classList.add("text-gray-700", "dark:text-gray-300", "border-blue-500")

        // Show/hide content
        document.querySelectorAll(".stats-tab-content").forEach(content => {
          content.classList.add("hidden")
        })
        document.getElementById(`tab-${tabName}`)?.classList.remove("hidden")
      })
    })
  }

  renderStats(stats) {
    // Render correlation ranking
    const correlationList = document.getElementById("correlation-list")
    if (correlationList && stats.correlation_ranking) {
      if (stats.correlation_ranking.length === 0) {
        correlationList.innerHTML = '<div class="text-xs text-gray-400 text-center py-2">データなし</div>'
      } else {
        correlationList.innerHTML = stats.correlation_ranking.map((item, idx) => `
          <button class="correlation-topic w-full text-left px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition flex items-center gap-2" data-topic-id="${item.id}">
            <span class="text-xs font-bold text-gray-400 w-4">${idx + 1}</span>
            <span class="text-xs text-gray-700 dark:text-gray-300 truncate flex-1">${item.name}</span>
            <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"><span class="w-1.5 h-1.5 rounded-full flex-shrink-0" style="background-color: ${this.catHex(item.category)}"></span>${item.connections}</span>
          </button>
        `).join("")

        // Add click handlers
        correlationList.querySelectorAll(".correlation-topic").forEach(btn => {
          btn.addEventListener("click", () => {
            const topicId = parseInt(btn.dataset.topicId)
            const topic = this.graphData?.nodes?.find(n => n.id === topicId)
            if (topic) {
              this.selectNode(topic, this.node, this.link, this.colorScale, this.cardWidth, this.cardHeight)
              this.panToNode(topic)
            }
          })
        })
      }
    }

    // Render media activity
    const mediaList = document.getElementById("media-list")
    if (mediaList && stats.media_activity) {
      if (stats.media_activity.length === 0) {
        mediaList.innerHTML = '<div class="text-xs text-gray-400 text-center py-2">データなし</div>'
      } else {
        const maxCount = stats.media_activity[0]?.count || 1
        mediaList.innerHTML = stats.media_activity.map((item, idx) => `
          <div class="flex items-center gap-2 py-1">
            <span class="text-xs font-bold text-gray-400 w-4">${idx + 1}</span>
            <span class="text-xs text-gray-700 dark:text-gray-300 w-16 truncate" title="${item.name}">${item.name}</span>
            <div class="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div class="bg-blue-500 h-1.5 rounded-full" style="width: ${(item.count / maxCount) * 100}%"></div>
            </div>
            <span class="text-xs text-gray-400 w-6 text-right">${item.count}</span>
          </div>
        `).join("")

        // Populate media filter dropdown with the same media list
        const mediaNames = stats.media_activity.map(m => m.name)
        this.populateMediaFilter(mediaNames)
      }
    }
  }

  renderGraph(data) {
    const container = document.getElementById("graph-container")
    const loading = document.getElementById("loading")
    const svgElement = document.getElementById("newspaper-graph")

    // 必須要素がない場合は終了（詳細ページ等での暫定対応）
    if (!container || !svgElement) {
      console.warn("Graph container or SVG element not found")
      if (loading) loading.innerHTML = '<div class="text-gray-500">グラフを表示できません</div>'
      return
    }

    if (!data.nodes.length) {
      if (loading) loading.innerHTML = '<div class="text-gray-500">この期間のニュースがありません</div>'
      return
    }

    if (loading) loading.classList.add("hidden")
    svgElement.classList.remove("hidden")

    // Update stats (要素が存在する場合のみ)
    const topicCountEl = document.getElementById("topic-count")
    const linkCountEl = document.getElementById("link-count")
    if (topicCountEl) topicCountEl.textContent = data.nodes.length
    if (linkCountEl) linkCountEl.textContent = data.links.length

    const svg = d3.select("#newspaper-graph")
    svg.selectAll("*").remove()

    const width = container.clientWidth
    const height = container.clientHeight

    svg.attr("width", width).attr("height", height)

    // Card size — カードの大きさも、下の力の強さも **SP と PC で同じ値**にする。
    // 一時期 SP だけ小さく詰めていたが、縮小したときの見え方が PC と別物になった。
    // 高さの下限 44 は動かさないこと。カードの中身は見出し(-h/2+24)と件数(h/2-8)を
    // 固定オフセットで置いているので、44 を割ると必ず文字同士が重なる。
    const maxSize = d3.max(data.nodes, d => d.size) || 10
    const cardWidth = d3.scaleLinear().domain([1, maxSize]).range([70, 140])
    const cardHeight = d3.scaleLinear().domain([1, maxSize]).range([44, 70])

    // ノードを置く「世界」の大きさ。
    // 以前は世界 = 画面ちょうど（tick の clamp が画面内に押し込めていた）で、
    // かつパンに制限が無かった。結果、SP では狭い箱に詰め込まれる一方、
    // 少し指を動かすと世界の外＝白紙に出てしまっていた。
    // ここではカードの総面積から世界を決め、パンをその世界の中に閉じ込める。
    const totalCardArea = d3.sum(data.nodes, d => cardWidth(d.size) * cardHeight(d.size))
    const targetArea = totalCardArea / WORLD_FILL_RATIO
    const aspect = width / height
    const worldWidth = Math.max(width, Math.sqrt(targetArea * aspect))
    const worldHeight = Math.max(height, Math.sqrt(targetArea / aspect))
    this.worldWidth = worldWidth
    this.worldHeight = worldHeight

    // 世界全体が画面に収まる倍率。これより引いても白い余白が増えるだけなので下限にする
    const fitScale = Math.min(width / worldWidth, height / worldHeight)

    // Reset animation flag
    this.nodesAnimated = false

    // Setup zoom
    this.zoom = d3.zoom()
      .scaleExtent([Math.min(fitScale, 1), 3])
      // パンの制限は外してある。世界は上下左右に繰り返す（トーラス）ので端が無い。
      // 描画側が「カメラに一番近い複製」を選ぶので、どこまで動かしてもカードが続く。
      // 引ける下限(scaleExtent)は残す。世界より広く見えると継ぎ目が画面に入るため
      .on("zoom", (event) => {
        this.zoomLevel = event.transform.k
        this.graphGroup.attr("transform", event.transform)
        // 収束後もパンで巻き戻しが効くように、ここでも描き直す
        this.redrawWrapped?.()
        this.updateZoomIndicator()
        this.updateMinimapViewport(event.transform)
      })

    svg.call(this.zoom)

    // 何も無い所を押したら絞り込みを解く。カードの上は selectNode が拾うので、
    // ここは「押した先が svg そのもの」の時だけに絞る
    svg.on("click", (event) => {
      if (event.target === svg.node() && this.selectedNode) this.clearSelection()
    })
      .on("dblclick.zoom", null) // Disable double-click zoom (we use it for expand)

    // Create main group for zoom/pan
    this.graphGroup = svg.append("g").attr("class", "graph-group")

    // Build adjacency map
    this.adjacencyMap = new Map()
    this.linkWeights = new Map()
    data.nodes.forEach(n => this.adjacencyMap.set(n.id, new Set()))
    data.links.forEach(l => {
      const sourceId = typeof l.source === "object" ? l.source.id : l.source
      const targetId = typeof l.target === "object" ? l.target.id : l.target
      this.adjacencyMap.get(sourceId)?.add(targetId)
      this.adjacencyMap.get(targetId)?.add(sourceId)
      this.linkWeights.set(`${sourceId}-${targetId}`, l.weight)
      this.linkWeights.set(`${targetId}-${sourceId}`, l.weight)
    })

    // Find hot topics (top 3 by size)
    const sortedBySize = [...data.nodes].sort((a, b) => b.size - a.size)
    this.hotTopicIds = new Set(sortedBySize.slice(0, 3).map(n => n.id))

    // Color scale
    const colorScale = d3.scaleOrdinal()
      .domain(Object.keys(this.categoryColorsValue))
      .range(Object.values(this.categoryColorsValue))
      .unknown("#9ca3af")

    // Force simulation
    // 反発力に対する「壁」は tick の clamp（世界の矩形）が担う。
    // 距離・反発・衝突半径は **SP と PC で同じ**。カードが動ける範囲と量を
    // 画面の幅で変えると、同じ倍率まで縮めたときに別物の絵になる。
    this.simulation = d3.forceSimulation(data.nodes)
      .force("link", d3.forceLink(data.links).id(d => d.id).distance(150).strength(0.2))
      .force("charge", d3.forceManyBody().strength(-300))
      .force("center", d3.forceCenter(worldWidth / 2, worldHeight / 2).strength(0.05))
      .force("collision", d3.forceCollide().radius(d => cardWidth(d.size) / 2 + 15))

    // Tooltip
    if (this.tooltip) this.tooltip.remove()
    this.tooltip = d3.select("body").append("div")
      .attr("class", "fixed bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none opacity-0 transition-opacity z-[100]")
      .style("transform", "translate(-50%, -100%)")

    // Build category map for cross-category link detection
    this.categoryMap = new Map()
    data.nodes.forEach(n => this.categoryMap.set(n.id, n.category))

    // Cross-category link detection helper
    const isCrossCategory = (link) => {
      const sourceId = typeof link.source === "object" ? link.source.id : link.source
      const targetId = typeof link.target === "object" ? link.target.id : link.target
      return this.categoryMap.get(sourceId) !== this.categoryMap.get(targetId)
    }

    // Links - cross-category links get special styling (視認性向上のためコントラスト強化)
    const link = this.graphGroup.append("g").attr("class", "links")
      .selectAll("line")
      .data(data.links)
      .enter()
      .append("line")
      .attr("stroke", d => isCrossCategory(d) ? "#9333ea" : "#9ca3af")
      .attr("stroke-opacity", d => isCrossCategory(d) ? 0.7 : 0.5)
      .attr("stroke-width", d => Math.max(1.5, Math.sqrt(d.weight) * 1.5))
      .attr("stroke-dasharray", d => isCrossCategory(d) ? "6,3" : null)
      .attr("stroke-linecap", "round")
      .on("mouseenter", (event, d) => this.showLinkTooltip(event, d, isCrossCategory(d)))
      .on("mouseleave", () => this.hideTooltip())

    // 探索の経路（道）。リンクの上・カードの下に敷く。
    // 矢印は marker で出すが、marker は stroke の色を継がないので renderTrail が塗り替える。
    // 区間の中点に置くので refX は中央(5)
    this.trailArrow = svg.append("defs").append("marker")
      .attr("id", "trail-arrow")
      .attr("viewBox", "0 0 10 10")
      .attr("refX", 5).attr("refY", 5)
      .attr("markerWidth", 5).attr("markerHeight", 5)
      .attr("orient", "auto-start-reverse")
      .append("path").attr("d", "M0,1 L10,5 L0,9 z")
    this.trailLayer = this.graphGroup.append("g").attr("class", "trail-layer")

    // Nodes with entrance animation
    const node = this.graphGroup.append("g").attr("class", "nodes")
      .selectAll("g")
      .data(data.nodes)
      .enter()
      .append("g")
      .attr("class", "card-node")
      .attr("cursor", "pointer")
      .attr("opacity", 0)
      .attr("transform", d => `translate(${width/2},${height/2}) scale(0.5)`)
      .call(d3.drag()
        .on("start", (e, d) => this.dragStarted(e, d))
        .on("drag", (e, d) => this.dragged(e, d))
        .on("end", (e, d) => this.dragEnded(e, d)))
      .on("click", (e, d) => {
        // 長押しで複数選択に入った直後は、その指離しのクリックを食わせない
        if (this.longPressFired) { this.longPressFired = false; return }
        if (this.multiSelect) this.toggleMultiSelect(d)
        else this.selectNode(d, node, link, colorScale, cardWidth, cardHeight)
      })
      .on("dblclick", (e, d) => this.expandFromNode(d))

    // Hot pulse
    node.filter(d => this.hotTopicIds.has(d.id))
      .append("rect")
      .attr("class", "hot-pulse")
      .attr("width", d => cardWidth(d.size) + 8)
      .attr("height", d => cardHeight(d.size) + 8)
      .attr("x", d => -cardWidth(d.size) / 2 - 4)
      .attr("y", d => -cardHeight(d.size) / 2 - 4)
      .attr("rx", 6)
      .attr("fill", "none")
      .attr("stroke", d => colorScale(d.category) || "#9ca3af")
      .attr("stroke-width", 2)
      .attr("stroke-opacity", 0.6)

    // Card shadow
    node.append("rect")
      .attr("class", "card-shadow")
      .attr("width", d => cardWidth(d.size))
      .attr("height", d => cardHeight(d.size))
      .attr("x", d => -cardWidth(d.size) / 2 + 3)
      .attr("y", d => -cardHeight(d.size) / 2 + 3)
      .attr("rx", 4)
      .attr("fill", "#00000015")

    // Card bg
    node.append("rect")
      .attr("class", "card-bg")
      .attr("width", d => cardWidth(d.size))
      .attr("height", d => cardHeight(d.size))
      .attr("x", d => -cardWidth(d.size) / 2)
      .attr("y", d => -cardHeight(d.size) / 2)
      .attr("rx", 4)
      .attr("fill", "#fff")
      .attr("stroke", d => colorScale(d.category) || "#9ca3af")
      .attr("stroke-width", 2)

    // Category bar
    node.append("rect")
      .attr("width", d => cardWidth(d.size))
      .attr("height", 4)
      .attr("x", d => -cardWidth(d.size) / 2)
      .attr("y", d => -cardHeight(d.size) / 2)
      .attr("rx", 4)
      .attr("fill", d => colorScale(d.category) || "#9ca3af")

    // Hot badge
    node.filter(d => this.hotTopicIds.has(d.id))
      .append("circle")
      .attr("cx", d => -cardWidth(d.size) / 2 + 8)
      .attr("cy", d => -cardHeight(d.size) / 2 - 4)
      .attr("r", 8)
      .attr("fill", "#f59e0b")

    node.filter(d => this.hotTopicIds.has(d.id))
      .append("text")
      .attr("x", d => -cardWidth(d.size) / 2 + 8)
      .attr("y", d => -cardHeight(d.size) / 2 - 1)
      .attr("text-anchor", "middle")
      .attr("font-size", "8")
      .attr("font-weight", "bold")
      .attr("fill", "#fff")
      .text("HOT")

    // Headline background for better readability
    node.append("rect")
      .attr("class", "headline-bg")
      .attr("x", d => -cardWidth(d.size) / 2 + 4)
      .attr("y", d => -cardHeight(d.size) / 2 + 10)
      .attr("width", d => cardWidth(d.size) - 8)
      .attr("height", 20)
      .attr("rx", 3)
      .attr("fill", "rgba(255, 255, 255, 0.85)")
      .attr("pointer-events", "none")

    // Headline (テキストは省略せずそのまま表示、枠からはみ出てもOK)
    node.append("text")
      .attr("class", "headline")
      .text(d => d.name)
      .attr("x", 0)
      .attr("y", d => -cardHeight(d.size) / 2 + 24)
      .attr("text-anchor", "middle")
      .attr("font-size", d => Math.min(11, cardWidth(d.size) / 6))
      .attr("font-weight", "700")
      .attr("fill", "#111827")
      .attr("pointer-events", "none")

    // Sparkline (trend graph)
    const self = this
    node.each(function(d) {
      if (!d.trend || d.trend.length < 2) return

      const g = d3.select(this)
      const sparkWidth = cardWidth(d.size) - 24
      const sparkHeight = 16
      const sparkY = cardHeight(d.size) / 2 - 28

      const xScale = d3.scaleLinear()
        .domain([0, d.trend.length - 1])
        .range([-sparkWidth / 2, sparkWidth / 2])

      const maxVal = Math.max(...d.trend, 1)
      const yScale = d3.scaleLinear()
        .domain([0, maxVal])
        .range([sparkY + sparkHeight / 2, sparkY - sparkHeight / 2])

      const line = d3.line()
        .x((_, i) => xScale(i))
        .y(v => yScale(v))
        .curve(d3.curveMonotoneX)

      // Area fill
      const area = d3.area()
        .x((_, i) => xScale(i))
        .y0(sparkY + sparkHeight / 2)
        .y1(v => yScale(v))
        .curve(d3.curveMonotoneX)

      const color = self.catHex(d.category)

      g.append("path")
        .attr("d", area(d.trend))
        .attr("fill", color)
        .attr("fill-opacity", 0.15)
        .attr("pointer-events", "none")

      g.append("path")
        .attr("d", line(d.trend))
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 1.5)
        .attr("stroke-opacity", 0.7)
        .attr("pointer-events", "none")

      // Last point dot
      const lastVal = d.trend[d.trend.length - 1]
      g.append("circle")
        .attr("cx", xScale(d.trend.length - 1))
        .attr("cy", yScale(lastVal))
        .attr("r", 2.5)
        .attr("fill", color)
        .attr("pointer-events", "none")
    })

    // Article count
    node.append("text")
      .attr("class", "article-count")
      .text(d => `${d.size}件`)
      .attr("x", 0)
      .attr("y", d => cardHeight(d.size) / 2 - 8)
      .attr("text-anchor", "middle")
      .attr("font-size", "10")
      .attr("fill", "#6b7280")
      .attr("pointer-events", "none")

    // Connection indicator
    node.append("circle")
      .attr("cx", d => cardWidth(d.size) / 2 - 10)
      .attr("cy", d => -cardHeight(d.size) / 2 + 10)
      .attr("r", 10)
      .attr("fill", "#f3f4f6")
      .attr("stroke", "#e5e7eb")

    node.append("text")
      .text(d => this.adjacencyMap.get(d.id)?.size || 0)
      .attr("x", d => cardWidth(d.size) / 2 - 10)
      .attr("y", d => -cardHeight(d.size) / 2 + 14)
      .attr("text-anchor", "middle")
      .attr("font-size", "10")
      .attr("font-weight", "600")
      .attr("fill", "#6b7280")
      .attr("pointer-events", "none")

    // Favorite star
    node.filter(d => this.favorites.includes(d.id))
      .append("text")
      .attr("class", "favorite-star")
      .attr("x", d => -cardWidth(d.size) / 2 + 12)
      .attr("y", d => cardHeight(d.size) / 2 - 8)
      .attr("font-size", "14")
      .attr("fill", "#eab308")
      .attr("pointer-events", "none")
      .text("★")

    // 「興味なし」にしたキーワードの印。**カードの大きさは変えない** —
    // 大きさは「記事が何本あるか」という事実の表示で、そこに好みを混ぜると
    // 本数が読めなくなる（docs/design/news-personalization.md）。
    // お気に入りの方は上の ★ が出す（ログイン中は this.favorites がサーバー由来）。
    node.filter(d => d.mark === "muted")
      .append("rect")
      .attr("class", "muted-ring")
      .attr("width", d => cardWidth(d.size) + 6)
      .attr("height", d => cardHeight(d.size) + 6)
      .attr("x", d => -cardWidth(d.size) / 2 - 3)
      .attr("y", d => -cardHeight(d.size) / 2 - 3)
      .attr("rx", 5)
      .attr("fill", "none")
      .attr("pointer-events", "none")
      .attr("stroke", "#9ca3af")
      .attr("stroke-width", 1.5)
      .attr("stroke-dasharray", "4 3")

    // 世界を上下左右に繰り返して見せる。力学は今までどおり世界の矩形の中で動かし、
    // **表示だけ**をカメラに一番近い複製に差し替える。layout は安定したまま、
    // スクロールが無限ループになる。
    //
    // **tick からだけ呼んではいけない。** d3 のシミュレーションは収束すると tick が
    // 止まるので、それだけだと落ち着いた後のパンで巻き戻しが効かず、画面が白紙になる。
    // ズーム/パンのイベントからも呼ぶこと。
    this.redrawWrapped = () => {
      this.applyWrapping(data.nodes, worldWidth, worldHeight)

      link
        .attr("x1", d => d.source.wx)
        .attr("y1", d => d.source.wy)
        // 線は短い方の回り道で引く。繋がった2つが世界の反対側にいるときに
        // 画面を横断させない
        .attr("x2", d => d.source.wx + wrapDelta(d.target.x - d.source.x, worldWidth))
        .attr("y2", d => d.source.wy + wrapDelta(d.target.y - d.source.y, worldHeight))
        // **両端とも画面の外にある線は描かない。**
        // ノードを増やすと、見えない者同士を結ぶ線が視界を横切って読めなくなる。
        // 片方でも見えていれば「どこかに繋がっている」ことは伝わるので残す。
        .attr("display", d => {
          const tx = d.source.wx + wrapDelta(d.target.x - d.source.x, worldWidth)
          const ty = d.source.wy + wrapDelta(d.target.y - d.source.y, worldHeight)
          return (this.onScreen(d.source.wx, d.source.wy) || this.onScreen(tx, ty)) ? null : "none"
        })

      node.attr("transform", d => `translate(${d.wx},${d.wy})`)
      this.updateTrailPositions()
    }

    // Tick
    this.simulation.on("tick", () => {
      data.nodes.forEach(d => {
        const hw = cardWidth(d.size) / 2 + 10
        const hh = cardHeight(d.size) / 2 + 10
        d.x = Math.max(hw, Math.min(worldWidth - hw, d.x))
        d.y = Math.max(hh, Math.min(worldHeight - hh, d.y))
      })

      this.redrawWrapped()

      // Entrance animation - fade in nodes as simulation settles
      if (!this.nodesAnimated && this.simulation.alpha() < 0.5) {
        this.nodesAnimated = true
        node.transition()
          .duration(600)
          .delay((d, i) => i * 30)
          .attr("opacity", 1)
      }

      // Update minimap periodically
      if (this.simulation.alpha() < 0.1) {
        this.updateMinimapNodes()
      }
    })

    // Store refs
    this.node = node
    this.link = link
    this.colorScale = colorScale
    this.cardWidth = cardWidth
    this.cardHeight = cardHeight
    this.svgWidth = width
    this.svgHeight = height

    this.startHotPulse()
    this.initMinimap(data)

    // 初期表示。以前は SP を 0.85 倍にしていたので、世界(=画面)が縮んで
    // 画面の周りに白い額縁が出ていた。世界の中心を画面の中心に置き、等倍以上で始める。
    // カードの大きさと力は PC と同じなので、狭い画面で 1.2 倍から始めると
    // 1画面に5〜6枚しか入らない。倍率だけは画面の広さで変える。
    const initialZoom = Math.max(fitScale, width < MOBILE_BREAKPOINT ? 1 : 1.2)
    this.initialTransform = d3.zoomIdentity
      .translate(width / 2, height / 2)
      .scale(initialZoom)
      .translate(-worldWidth / 2, -worldHeight / 2)
    svg.call(this.zoom.transform, this.initialTransform)

    // 画面のリサイズなどで描き直したときは trailLayer も作り直されている。
    // 経路そのものは残っているので引き直す（引き直さないと道だけ消えて数字が残る）
    this.renderTrail()
  }

  // Zoom controls
  zoomIn() {
    const svg = d3.select("#newspaper-graph")
    svg.transition().duration(300).call(this.zoom.scaleBy, 1.3)
  }

  zoomOut() {
    const svg = d3.select("#newspaper-graph")
    svg.transition().duration(300).call(this.zoom.scaleBy, 0.7)
  }

  resetZoom() {
    const svg = d3.select("#newspaper-graph")
    // zoomIdentity は世界の左上に戻る。世界が画面より広い今は、それだと端に飛ぶ
    svg.transition().duration(300).call(this.zoom.transform, this.initialTransform || d3.zoomIdentity)
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        this.showToast("フルスクリーンにできませんでした")
      })
    } else {
      document.exitFullscreen()
    }
  }

  updateFullscreenUI() {
    const icon = document.getElementById("fullscreen-icon")
    const label = document.getElementById("fullscreen-label")
    const isFullscreen = !!document.fullscreenElement

    if (icon) {
      icon.innerHTML = isFullscreen
        ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"></path>'
        : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>'
    }
    if (label) {
      label.textContent = isFullscreen ? "終了" : "全画面"
    }
  }

  updateZoomIndicator() {
    const indicator = document.getElementById("zoom-level")
    if (indicator) {
      indicator.textContent = Math.round(this.zoomLevel * 100) + "%"
    }
  }

  expandFromNode(d) {
    // Double-click: zoom to node and show only connected
    const svg = d3.select("#newspaper-graph")
    const scale = 1.5
    const x = this.svgWidth / 2 - d.x * scale
    const y = this.svgHeight / 2 - d.y * scale

    svg.transition().duration(500)
      .call(this.zoom.transform, d3.zoomIdentity.translate(x, y).scale(scale))
  }

  startHotPulse() {
    const pulse = () => {
      d3.selectAll(".hot-pulse")
        .transition().duration(1000)
        .attr("stroke-opacity", 0.8).attr("stroke-width", 4)
        .transition().duration(1000)
        .attr("stroke-opacity", 0.3).attr("stroke-width", 2)
        .on("end", pulse)
    }
    pulse()
  }

  truncateText(text, maxWidth) {
    const maxChars = Math.floor(maxWidth / 10)
    return text.length <= maxChars ? text : text.substring(0, maxChars - 1) + "…"
  }

  showLinkTooltip(event, d, isCross = false) {
    const sourceId = typeof d.source === "object" ? d.source.id : d.source
    const targetId = typeof d.target === "object" ? d.target.id : d.target
    const sourceNode = this.graphData.nodes.find(n => n.id === sourceId)
    const targetNode = this.graphData.nodes.find(n => n.id === targetId)
    const sourceName = sourceNode?.name || ""
    const targetName = targetNode?.name || ""

    const crossLabel = isCross
      ? `<br><span class="text-purple-300">カテゴリ横断: ${sourceNode ? this.catName(sourceNode.category) : ""} × ${targetNode ? this.catName(targetNode.category) : ""}</span>`
      : ""

    this.tooltip
      .html(`<strong>${sourceName}</strong> ↔ <strong>${targetName}</strong><br>共通記事: ${d.weight}件${crossLabel}`)
      .style("left", event.pageX + "px")
      .style("top", (event.pageY - 10) + "px")
      .style("opacity", 1)
  }

  hideTooltip() {
    this.tooltip.style("opacity", 0)
  }

  discoverRandom() {
    if (!this.graphData?.nodes?.length) return
    const randomNode = this.graphData.nodes[Math.floor(Math.random() * this.graphData.nodes.length)]
    this.selectNode(randomNode, this.node, this.link, this.colorScale, this.cardWidth, this.cardHeight)
  }

  // ── 探索の足跡 ────────────────────────────────────────────────────────
  // 辿った経路を「グラフの上の道」として見せる。仕様と、なぜ帯をやめたかは
  // docs/design/news-exploration-trail.md を参照。

  // 経路に積む規則は2つだけ。
  //   すでに経路上にある  → そこまで切り詰める（＝戻る）
  //   経路上に無い        → 末尾に積む
  // これで往復(A→B→A)が「積む」ではなく「戻る」になり、経路がノイズにならない。
  pushTrail(topic) {
    const at = this.trail.indexOf(topic.id)
    if (at >= 0) {
      this.trail.length = at + 1
    } else {
      this.trail.push(topic.id)
      // 上限は8区間 = ノード9個。超えたら古い方から落とす
      if (this.trail.length > TRAIL_MAX_SEGMENTS + 1) this.trail.shift()
    }
    this.renderTrail()
  }

  // 1つ戻る。経路が2つ以上のときだけ意味がある
  trailBack() {
    if (this.trail.length < 2) return
    const prevId = this.trail[this.trail.length - 2]
    const prev = this.graphData?.nodes.find(n => n.id === prevId)
    if (!prev) return
    // selectNode → pushTrail で「経路上にある」判定に入り、末尾が1つ外れる
    this.selectNode(prev, this.node, this.link, this.colorScale, this.cardWidth, this.cardHeight)
  }

  clearTrail() {
    this.trail = []
    this.renderTrail()
  }

  // 道とバッジを引き直す。座標の追従は tick 側（updateTrailPositions）が持つ
  renderTrail() {
    // 今のグラフに居ないトピックは経路から落とす。表示している数と道の本数を
    // 食い違わせないための保険（通常は loadGraph 側で経路ごと捨てている）
    if (this.graphData) {
      const alive = new Set(this.graphData.nodes.map(n => n.id))
      this.trail = this.trail.filter(id => alive.has(id))
    }

    const control = document.getElementById("trail-control")
    const count = document.getElementById("trail-count")
    const back = document.getElementById("trail-back")
    const segments = Math.max(0, this.trail.length - 1)

    if (control) control.classList.toggle("hidden", segments === 0)
    if (count) count.textContent = `${segments} / ${TRAIL_MAX_SEGMENTS}`
    if (back) back.disabled = segments === 0

    if (!this.trailLayer) return

    const nodes = this.trail.map(id => this.graphData?.nodes.find(n => n.id === id)).filter(Boolean)
    const segs = nodes.slice(0, -1).map((from, i) => ({ from, to: nodes[i + 1], i }))
    const hex = this.catHex(nodes[nodes.length - 1]?.category)

    // 矢印の色は道と揃える。marker は色を継がないので、その都度塗り替える
    this.trailArrow?.attr("fill", hex)

    // line ではなく path を使い、矢印は marker-end ではなく **区間の中点**(marker-mid)に出す。
    // 終端はカードの中心なので、marker-end だと矢印がカードの下に隠れて見えない
    const lines = this.trailLayer.selectAll("path.trail-seg").data(segs, d => `${d.from.id}-${d.to.id}`)
    lines.exit().remove()
    lines.enter().append("path")
      .attr("class", "trail-seg")
      .attr("fill", "none")
      .attr("stroke-linecap", "round")
      .attr("marker-mid", "url(#trail-arrow)")
      .attr("pointer-events", "none")
      .merge(lines)
      .attr("stroke", hex)
      .attr("stroke-width", 3)
      // 新しい区間ほど濃く、古いほど薄く
      .attr("stroke-opacity", d => Math.max(0.25, 0.9 - (segs.length - 1 - d.i) * 0.12))

    // 区間が0（＝まだ1つ選んだだけ）ならバッジも出さない。
    // コントロールも隠れている状態なので、番号だけがカードに残ると意味が読めない
    const badges = this.trailLayer.selectAll("g.trail-badge").data(segs.length ? nodes : [], d => d.id)
    badges.exit().remove()
    const enter = badges.enter().append("g")
      .attr("class", "trail-badge")
      .attr("pointer-events", "none")
    enter.append("circle").attr("r", 8)
    enter.append("text")
      .attr("text-anchor", "middle").attr("y", 3)
      .attr("font-size", "9").attr("font-weight", "700").attr("fill", "#fff")

    const merged = enter.merge(badges)
    merged.select("circle").attr("fill", hex)
    merged.select("text").text((d, i) => i + 1)

    this.updateTrailPositions()
  }

  // 世界の座標が画面に入っているか。線を間引くのに使う
  onScreen(x, y) {
    if (!this.viewBounds) return true

    const { left, top, right, bottom } = this.viewBounds
    return x >= left && x <= right && y >= top && y <= bottom
  }

  // カメラの中心から見て一番近い複製の座標を wx / wy に入れる。
  // 世界より狭い画面で見ている限り、切り替わりは画面の外で起きるので継ぎ目は見えない。
  applyWrapping(nodes, worldWidth, worldHeight) {
    const svgNode = document.getElementById("newspaper-graph")
    if (!svgNode) return

    const transform = d3.zoomTransform(svgNode)
    const [ cx, cy ] = transform.invert([ this.svgWidth / 2, this.svgHeight / 2 ])

    // 画面に映っている世界の範囲。少し外まで含めて、端の線が急に消えないようにする
    const [ left, top ] = transform.invert([ -VIEW_MARGIN, -VIEW_MARGIN ])
    const [ right, bottom ] = transform.invert([ this.svgWidth + VIEW_MARGIN, this.svgHeight + VIEW_MARGIN ])
    this.viewBounds = { left, top, right, bottom }

    nodes.forEach(d => {
      d.wx = cx + wrapDelta(d.x - cx, worldWidth)
      d.wy = cy + wrapDelta(d.y - cy, worldHeight)
    })
  }

  // ノードは力学で動き続けるので、道とバッジは tick ごとに座標を合わせる
  updateTrailPositions() {
    if (!this.trailLayer) return

    // 中点を明示的に打つ。marker-mid はこの点に出る
    // 道もラップ後の座標で引く（wx が未計算のうちは素の座標で代用）
    const px = (n) => (n.wx === undefined ? n.x : n.wx)
    const py = (n) => (n.wy === undefined ? n.y : n.wy)
    this.trailLayer.selectAll("path.trail-seg")
      .attr("d", d => {
        const x1 = px(d.from), y1 = py(d.from)
        const x2 = px(d.to), y2 = py(d.to)
        return `M${x1},${y1} L${(x1 + x2) / 2},${(y1 + y2) / 2} L${x2},${y2}`
      })

    const cw = this.cardWidth, ch = this.cardHeight
    this.trailLayer.selectAll("g.trail-badge")
      // カードの右下の外側。左上の外側は HOT バッジが使っている
      .attr("transform", d => `translate(${px(d) + cw(d.size) / 2 - 8},${py(d) + ch(d.size) / 2 + 4})`)
  }

  // 選んだキーワードからの距離で3段に分ける。
  //   0(自分) と 1(直接つながっている) … そのまま出す
  //   2(その先)                        … 薄く出す
  //   それ以外                          … 出さない
  // 薄い段を残すのは、「まだ先がある」ことが見えないと、絞った瞬間に
  // グラフが行き止まりに見えてしまうから。
  computeNeighborTiers(id) {
    const first = new Set(this.adjacencyMap?.get(id) || [])
    const second = new Set()
    first.forEach(fid => {
      (this.adjacencyMap?.get(fid) || new Set()).forEach(sid => {
        if (sid !== id && !first.has(sid)) second.add(sid)
      })
    })
    return { first, second }
  }

  nodeOpacityFor(nodeId) {
    if (this.multiSelect) return this.multiOpacityFor(nodeId)
    if (!this.selectedNode) return 1
    if (nodeId === this.selectedNode.id || this.connectedNodes.has(nodeId)) return 1
    // 集中モードは「その先」も落として、直接つながっているものだけにする
    if (this.secondDegreeNodes?.has(nodeId)) return SECOND_DEGREE_OPACITY
    return 0
  }

  // ノードと線の見え方を、上の段の判定にそろえて一度に塗る。
  // **線と点で別々に transition を掛けないこと** — 同じ選択に2本流すと
  // 片方が中断されて、消えたはずの線が残る
  applyNeighborTiers(duration = 300) {
    if (!this.node || !this.link) return
    if (!this.selectedNode && !this.multiSelect) return
    // 太く色を付ける「中心」は、1つ選びならその1つ、複数選びなら選んだ全部
    const isCenter = id => this.multiSelect ? this.multiIds.has(id) : id === this.selectedNode?.id
    const centerCategory = this.multiSelect
      ? this.graphData?.nodes?.find(n => this.multiIds.has(n.id))?.category
      : this.selectedNode?.category
    const ends = l => [ typeof l.source === "object" ? l.source.id : l.source,
                        typeof l.target === "object" ? l.target.id : l.target ]

    this.node.transition().duration(duration)
      .attr("opacity", n => this.nodeOpacityFor(n.id))
      // 消えているカードを掴めてしまうと、何も無い所を押したのに記事が出る
      .attr("pointer-events", n => this.nodeOpacityFor(n.id) === 0 ? "none" : "all")

    this.link.transition().duration(duration)
      .attr("stroke", l => {
        const [s, t] = ends(l)
        return (isCenter(s) || isCenter(t)) ? (this.catHex(centerCategory) || "#3b82f6") : "#9ca3af"
      })
      .attr("stroke-width", l => {
        const [s, t] = ends(l)
        // 選んだもの同士を結ぶ線は一番太く。「なぜこの2つが繋がるのか」がそこに出る
        if (isCenter(s) && isCenter(t)) return Math.sqrt(l.weight) * 4
        return (isCenter(s) || isCenter(t)) ? Math.sqrt(l.weight) * 3 : Math.sqrt(l.weight) * 1.5
      })
      .attr("stroke-opacity", 0.8)
      .attr("opacity", l => {
        const [s, t] = ends(l)
        const os = this.nodeOpacityFor(s), ot = this.nodeOpacityFor(t)
        // 端が消えている線は引かない。残す線は薄い側に合わせる
        return (os === 0 || ot === 0) ? 0 : Math.min(os, ot)
      })
  }

  selectNode(d, node, link, colorScale, cardWidth, cardHeight) {
    document.getElementById("instructions")?.classList.add("opacity-0", "pointer-events-none")
    this.pushTrail(d)

    const tiers = this.computeNeighborTiers(d.id)
    this.connectedNodes = tiers.first
    this.secondDegreeNodes = tiers.second
    this.selectedNode = d

    // Update URL for sharing
    const url = new URL(window.location)
    url.searchParams.set("topic", d.id)
    window.history.replaceState({}, "", url)

    node.select(".card-bg")
      .transition().duration(300)
      .attr("fill", n => n.id === d.id ? "#fef3c7" : this.connectedNodes.has(n.id) ? "#f0fdf4" : "#fff")
      .attr("stroke-width", n => n.id === d.id ? 3 : 2)

    this.hideFilterChip()   // パネルが開くので、そこに名前が出ている
    document.getElementById("panel-footer")?.classList.remove("hidden")
    this.applyNeighborTiers(300)
    // **縮めるのが先。** 後にすると、全画面の中央＝シートの裏に選んだカードが行く
    this.setGraphHalf(true)
    this.pullConnectedNodes(d)
    this.showArticlePanel(d)
  }

  pullConnectedNodes(centerNode) {
    const container = document.getElementById("graph-container")
    // 「画面の中央」を世界の座標へ直してから引き寄せる。
    // 画面座標をそのまま世界の座標として使っていたので、パンやズームをしていると
    // 選んだノードが画面の外へ飛んでいた（実測: パン3回で固定先が画面外 -624,-782）
    const transform = d3.zoomTransform(document.getElementById("newspaper-graph"))
    const [centerX, centerY] = transform.invert([container.clientWidth / 2, container.clientHeight / 2])

    centerNode.fx = centerX
    centerNode.fy = centerY

    this.simulation.force("link").distance(l => {
      const sId = typeof l.source === "object" ? l.source.id : l.source
      const tId = typeof l.target === "object" ? l.target.id : l.target
      return (sId === centerNode.id || tId === centerNode.id) ? 80 : 150
    })

    this.simulation.force("attract", d3.forceRadial(120, centerX, centerY)
      .strength(n => this.connectedNodes.has(n.id) ? 0.3 : 0))

    this.simulation.alpha(0.5).restart()

    setTimeout(() => {
      centerNode.fx = null
      centerNode.fy = null
      this.simulation.force("attract", null)
    }, 1500)
  }

  async showArticlePanel(topicData) {
    const panel = document.getElementById("article-panel")
    const title = document.getElementById("panel-title")
    const content = document.getElementById("panel-content")
    const viewAllLink = document.getElementById("view-all-link")

    // 要素が存在しない場合は終了
    if (!panel || !title || !content) return

    panel.classList.remove("translate-y-full", "sm:translate-x-full")
    this.setSheetExpanded(false)   // 開くときは必ず半分から
    title.textContent = topicData.name

    // クラスタノードの場合は含まれるトピック一覧を表示
    if (topicData.is_cluster && topicData.cluster_topics) {
      viewAllLink.style.display = "none"
      content.innerHTML = `
        <div class="space-y-2">
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
            ${topicData.cluster_topics.length}件の関連トピック
          </p>
          ${topicData.cluster_topics.map(t => `
            <button class="cluster-topic-btn w-full text-left px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition" data-topic-id="${t.id}">
              <span class="text-sm font-medium text-gray-900 dark:text-white">${t.name}</span>
            </button>
          `).join("")}
        </div>
      `
      // クラスタ内トピックのクリックハンドラ
      content.querySelectorAll(".cluster-topic-btn").forEach(btn => {
        btn.addEventListener("click", () => {
          const topicId = parseInt(btn.dataset.topicId)
          window.location.href = `/news/topics/${topicId}`
        })
      })
      return
    }

    viewAllLink.style.display = ""
    viewAllLink.href = `/news/topics/${topicData.id}`

    content.innerHTML = '<div class="animate-pulse space-y-4"><div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div><div class="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div></div>'

    try {
      const previewUrl = new URL(`/news/topics/${topicData.id}/preview`, window.location.origin)
      if (this.hoursValue > 0) {
        previewUrl.searchParams.set("hours", this.hoursValue)
      } else {
        previewUrl.searchParams.set("days", this.daysValue)
      }
      const response = await fetch(previewUrl)
      if (!response.ok) throw new Error(response.status)
      const data = await response.json()
      this.renderArticles(data, content)
    } catch (error) {
      content.innerHTML = '<div class="text-red-500">読み込みに失敗しました</div>'
    }
  }

  renderArticles(data, container) {
    const borderColorHex = this.catHex(data.topic.category)

    // Trend chart at top
    const topicNode = this.graphData?.nodes?.find(n => n.id === data.topic.id)
    let trendHtml = ""
    if (topicNode?.trend && topicNode.trend.length > 1) {
      const trend = topicNode.trend
      const maxVal = Math.max(...trend, 1)
      const color = this.catHex(data.topic.category)

      // Create SVG path for trend
      const width = 280
      const height = 50
      const points = trend.map((v, i) => {
        const x = (i / (trend.length - 1)) * width
        const y = height - (v / maxVal) * height * 0.8 - 5
        return `${x},${y}`
      })

      const areaPoints = [`0,${height}`, ...points, `${width},${height}`].join(" ")
      const linePoints = points.join(" ")

      // Calculate trend direction
      const recentAvg = trend.slice(-3).reduce((a, b) => a + b, 0) / 3
      const oldAvg = trend.slice(0, 3).reduce((a, b) => a + b, 0) / 3
      const trendDir = recentAvg > oldAvg ? "↑" : recentAvg < oldAvg ? "↓" : "→"
      const trendClass = recentAvg > oldAvg ? "text-green-600" : recentAvg < oldAvg ? "text-red-600" : "text-gray-500"

      trendHtml = `
        <div id="panel-trend" class="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-bold text-gray-500 uppercase">過去${trend.length}日間のトレンド</span>
            <span class="text-lg font-bold ${trendClass}">${trendDir}</span>
          </div>
          <svg viewBox="0 0 ${width} ${height}" class="w-full h-12">
            <polygon points="${areaPoints}" fill="${color}" fill-opacity="0.15"/>
            <polyline points="${linePoints}" fill="none" stroke="${color}" stroke-width="2" stroke-linejoin="round"/>
            <circle cx="${width}" cy="${height - (trend[trend.length-1] / maxVal) * height * 0.8 - 5}" r="4" fill="${color}"/>
          </svg>
          <div class="flex justify-between text-xs text-gray-400 mt-1">
            <span>${trend.length}日前</span>
            <span>今日</span>
          </div>
        </div>
      `
    }

    // Media distribution
    const mediaCounts = {}
    data.articles.forEach(a => mediaCounts[a.media_name] = (mediaCounts[a.media_name] || 0) + 1)
    const mediaEntries = Object.entries(mediaCounts).sort((a, b) => b[1] - a[1])

    // Sort options
    const sortHtml = data.articles.length > 1 ? `
      <div class="flex items-center justify-between mb-3">
        <span class="text-xs text-gray-500">${data.articles.length}件の記事</span>
        <select id="article-sort" class="text-xs bg-gray-100 dark:bg-gray-800 border-0 rounded px-2 py-1 text-gray-600 dark:text-gray-400">
          <option value="date">新着順</option>
          <option value="media">メディア順</option>
        </select>
      </div>
    ` : ""

    let articlesHtml = data.articles.length
      ? sortHtml + data.articles.map(a => {
          const isDemo = a.url.includes("example.com")
          // 外部メディアへ直接出さず、先に記事の詳細ページ（何と繋がっているか）を見せる。
          // 外部リンクはその一番下にある。docs/design/news-article-detail.md
          return `
          <a href="${isDemo ? '#' : `/news/articles/${a.id}`}"
             class="article-link block border-l-4 bg-gray-50 dark:bg-gray-800 p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition mb-3 ${isDemo ? 'cursor-default' : ''}"
             style="border-left-color: ${borderColorHex}"
             data-demo="${isDemo}">
            <h4 class="font-bold text-gray-900 dark:text-white text-sm leading-snug mb-1">${a.title}</h4>
            <div class="flex items-center gap-2 text-xs text-gray-500">
              ${a.media_name} · ${a.published_at}
              ${isDemo ? '<span class="px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 rounded text-xs">デモ</span>' : ''}
            </div>
            ${a.summary ? `<p class="text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">${a.summary}</p>` : ''}
          </a>`
        }).join("")
      : '<div class="text-center py-8 text-gray-500">この期間の記事はありません</div>'

    // Media chart
    let mediaHtml = mediaEntries.length ? `
      <div class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h4 class="text-xs font-bold text-gray-500 uppercase mb-3">メディア分布</h4>
        <div class="space-y-2">
          ${mediaEntries.slice(0, 5).map(([name, count]) => `
            <div class="flex items-center gap-2">
              <span class="text-xs text-gray-600 dark:text-gray-400 w-20 truncate">${name}</span>
              <div class="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div class="bg-blue-500 h-2 rounded-full" style="width: ${(count / mediaEntries[0][1]) * 100}%"></div>
              </div>
              <span class="text-xs text-gray-500 w-6 text-right">${count}</span>
            </div>
          `).join("")}
        </div>
      </div>
    ` : ""

    // Connected topics
    let connectedHtml = ""
    if (this.connectedNodes.size > 0) {
      const connected = this.graphData.nodes.filter(n => this.connectedNodes.has(n.id))
      connectedHtml = `
        <div class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 class="text-xs font-bold text-gray-500 uppercase mb-2">関連トピック (${connected.length})</h4>
          <div class="flex flex-wrap gap-2">
            ${connected.map(t => {
              const w = this.linkWeights.get(`${data.topic.id}-${t.id}`) || this.linkWeights.get(`${t.id}-${data.topic.id}`) || 0
              return `<button class="related-topic inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:opacity-80 transition" data-topic-id="${t.id}"><span class="w-1.5 h-1.5 rounded-full flex-shrink-0" style="background-color: ${this.catHex(t.category)}"></span>${t.name} (${w})</button>`
            }).join("")}
          </div>
        </div>
      `
    }

    // 未ログインの人には、★が端末にしか残らないことと、その直し方を出す。
    // ログイン中は下の「興味なし」ボタンが出る（★は下の actionsHtml が持つ）
    const interestHtml = data.logged_in
      ? `
      <div class="mt-4">
        <button id="mute-btn" data-topic-id="${data.topic.id}" data-muted="${data.topic.muted}"
                class="w-full py-2 px-3 rounded text-xs font-bold transition ${data.topic.muted ? "bg-gray-600 text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"}">
          ${data.topic.muted ? "興味なしを解除" : "興味なし（後ろに下げる）"}
        </button>
        <p class="text-[11px] text-gray-400 mt-1">下げるだけで、グラフからは消しません</p>
      </div>`
      : `
      <div class="mt-4">
        <a href="/login?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}"
           class="block text-center text-xs text-blue-600 dark:text-blue-400 hover:underline">
          ログインすると、お気に入りが端末をまたいで残り、トップの並びにも効きます
        </a>
      </div>`

    // Action buttons
    // ログイン中はサーバーが返す favorited が正（this.favorites は表示用の写し）
    const isFavorite = data.logged_in ? !!data.topic.favorited : this.favorites.includes(data.topic.id)
    const actionsHtml = `
      <div class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex gap-2">
        <button id="favorite-btn" data-topic-id="${data.topic.id}" class="flex-1 py-2 px-4 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center justify-center gap-2 transition">
          ${isFavorite
            ? '<svg class="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>'
            : '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>'}
          お気に入り
        </button>
        <button id="share-btn" class="flex-1 py-2 px-4 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center justify-center gap-2 transition">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path></svg>
          共有
        </button>
      </div>
    `

    container.innerHTML = trendHtml + articlesHtml + mediaHtml + connectedHtml + actionsHtml + interestHtml
    this.applyTrendVisibility()
    this.setQualityVote(data.topic, data.logged_in)

    // 「興味なし」。もう一度押すと解除
    container.querySelector("#mute-btn")?.addEventListener("click", e => this.toggleMute(e.currentTarget))

    // Demo article click handler
    container.querySelectorAll(".article-link[data-demo='true']").forEach(link => {
      link.addEventListener("click", (e) => {
        e.preventDefault()
        this.showToast("デモデータのため、実際の記事ページはありません")
      })
    })

    // Event listeners
    container.querySelectorAll(".related-topic").forEach(btn => {
      btn.addEventListener("click", () => {
        const topic = this.graphData.nodes.find(n => n.id === parseInt(btn.dataset.topicId))
        if (topic) this.selectNode(topic, this.node, this.link, this.colorScale, this.cardWidth, this.cardHeight)
      })
    })

    document.getElementById("share-btn")?.addEventListener("click", () => this.copyShareUrl())
    document.getElementById("favorite-btn")?.addEventListener("click", () => {
      this.toggleFavorite(data.topic.id)
    })

    // Sort handler
    document.getElementById("article-sort")?.addEventListener("change", (e) => {
      const articles = [...container.querySelectorAll(".article-link")]
      const parent = articles[0]?.parentElement
      if (!parent) return

      articles.sort((a, b) => {
        if (e.target.value === "media") {
          const mediaA = a.querySelector(".text-gray-500")?.textContent || ""
          const mediaB = b.querySelector(".text-gray-500")?.textContent || ""
          return mediaA.localeCompare(mediaB)
        }
        return 0 // Already sorted by date
      })
      articles.forEach(el => parent.appendChild(el))
    })
  }

  // 5分ごとの自動チェック。
  //
  // 読み手のブラウザは**取り込みを起こさない**（サーバー側が管理者以外には
  // ingested:false と最新記事の時刻だけを返す）。以前はこれが全タブから
  // 5分ごとに配信元の巡回を起こしていた。
  // 理由: docs/reviews/2026-08-31-owasp-gamefactory-portal.md #1
  async silentRefresh() {
    try {
      const data = await this.postRefresh()
      if (!data?.success) return

      if (data.ingested) {
        // 管理者が押した/回した時だけ、取り込んだ件数がそのまま返る
        if (data.created > 0) {
          this.showToast(`${data.created}件の新着ニュース`)
          this.loadGraph()
        }
        return
      }

      // 読み手側: 最新記事の時刻が前回と変わっていたら新着があったということ
      if (this.lastLatestAt && data.latest_at && data.latest_at !== this.lastLatestAt) {
        this.showToast("新しいニュースが届きました")
        this.loadGraph()
      }
      this.lastLatestAt = data.latest_at ?? this.lastLatestAt
    } catch (e) {
      // Silent fail
    }
  }

  async postRefresh() {
    const response = await fetch("/news/topics/refresh", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      credentials: "same-origin"
    })
    if (!response.ok) return null
    return response.json()
  }

  // --- 関心まわりの通信 ------------------------------------------------
  // ログイン中はサーバー、未ログインは今までどおり localStorage。
  // 仕様: docs/design/news-personalization.md

  async apiJson(url, method, body = null) {
    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      credentials: "same-origin",
      body: body ? JSON.stringify(body) : null
    })
    if (res.status === 401) {
      window.location.href = `/login?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`
      return null
    }
    return res.json()
  }

  // サーバーが付けた印を、描画に使う手元の配列へ写す。
  // 今の画面に出ているノードの分しか分からないが、★もフィルタも
  // 「画面に出ているもの」にしか効かないので足りる。
  syncMarksFromServer() {
    if (!this.loggedIn || !this.graphData?.nodes) return
    this.favorites = this.graphData.nodes.filter(n => n.mark === "favorite").map(n => n.id)
  }

  // 端末に貯めた★をアカウントへ移す。**一度だけ。**
  // 移し終えたら true を返す（呼んだ側がグラフを取り直す）。
  async migrateLocalFavorites() {
    if (!this.loggedIn) return false
    if (localStorage.getItem("favoriteTopicsMigrated") === "1") return false

    const local = JSON.parse(localStorage.getItem("favoriteTopics") || "[]")
    if (local.length === 0) {
      localStorage.setItem("favoriteTopicsMigrated", "1")
      return false
    }

    // 数が多い端末で大量に投げないよう上限を切る（★は普通そんなに付かない）
    for (const id of local.slice(0, 200)) {
      try {
        await this.apiJson(`/news/topic_favorites/${id}`, "POST")
      } catch (e) {
        console.error("favorite migration failed", id, e)
        return false   // 途中で失敗したらフラグを立てない（次回やり直す）
      }
    }
    localStorage.setItem("favoriteTopicsMigrated", "1")
    this.showToast(`この端末のお気に入り ${local.length} 件をアカウントに移しました`)
    return true
  }

  async toggleMute(btn) {
    if (!btn || btn.dataset.busy === "1") return
    btn.dataset.busy = "1"
    const topicId = btn.dataset.topicId
    const muted = btn.dataset.muted === "true"
    try {
      const json = await this.apiJson(
        `/news/topic_preferences/${topicId}`,
        muted ? "DELETE" : "PUT",
        muted ? null : { stance: "down" }
      )
      if (!json || json.ok === false) return

      btn.dataset.muted = String(json.muted)
      btn.textContent = json.muted ? "興味なしを解除" : "興味なし（後ろに下げる）"
      btn.classList.toggle("bg-gray-600", json.muted)
      btn.classList.toggle("text-white", json.muted)
      btn.classList.toggle("bg-gray-100", !json.muted)
      btn.classList.toggle("dark:bg-gray-800", !json.muted)

      // 「興味なし」を付けるとお気に入りは外れる（サーバー側もそうしている）
      if (json.muted) {
        this.favorites = this.favorites.filter(id => String(id) !== String(topicId))
        this.updateFavoriteUI(Number(topicId))
      }

      // 手元のデータにも書いておく（次に描き直した時に印が合う）。
      // **並び順はその場で動かさない。** 押した瞬間にカードが飛んでいくと
      // 何を押したのか分からなくなる。並びが変わるのは次に読み込んだ時
      const node = this.graphData?.nodes?.find(n => String(n.id) === String(topicId))
      if (node) node.mark = json.muted ? "muted" : undefined

      this.showToast(json.muted ? "次から後ろに下げます" : "興味なしを解除しました")
    } catch (e) {
      console.error("mute update failed", e)
    } finally {
      btn.dataset.busy = "0"
    }
  }

  async toggleFavorite(topicId) {
    const on = this.favorites.includes(topicId)

    if (this.loggedIn) {
      const json = await this.apiJson(
        `/news/topic_favorites/${topicId}`,
        on ? "DELETE" : "POST"
      )
      if (!json || json.ok === false) return

      if (json.favorited) {
        if (!on) this.favorites.push(topicId)
        // お気に入りにすると「興味なし」は外れる（サーバー側もそうしている）
        const node = this.graphData?.nodes?.find(n => String(n.id) === String(topicId))
        if (node) node.mark = "favorite"
        const muteBtn = document.getElementById("mute-btn")
        if (muteBtn && String(muteBtn.dataset.topicId) === String(topicId)) {
          muteBtn.dataset.muted = "false"
          muteBtn.textContent = "興味なし（後ろに下げる）"
          muteBtn.classList.remove("bg-gray-600", "text-white")
          muteBtn.classList.add("bg-gray-100", "dark:bg-gray-800")
        }
      } else {
        this.favorites = this.favorites.filter(id => id !== topicId)
        const node = this.graphData?.nodes?.find(n => String(n.id) === String(topicId))
        if (node && node.mark === "favorite") node.mark = undefined
      }
      this.showToast(json.favorited ? "お気に入りに追加しました（次から前に出します）" : "お気に入りから削除しました")
      this.updateFavoriteUI(topicId)
      return
    }

    // 未ログインは今までどおり端末に貯める（ログインした時に移行する）
    if (!on) {
      this.favorites.push(topicId)
      this.showToast("お気に入りに追加しました（この端末だけ）")
    } else {
      this.favorites = this.favorites.filter(id => id !== topicId)
      this.showToast("お気に入りから削除しました")
    }
    localStorage.setItem("favoriteTopics", JSON.stringify(this.favorites))
    this.updateFavoriteUI(topicId)
  }

  toggleFavoritesFilter() {
    this.showOnlyFavorites = !this.showOnlyFavorites
    const btns = [...document.querySelectorAll(".favorites-filter-btn")]
    const setOn = on => btns.forEach(b => {
      b.classList.toggle("bg-yellow-100", on)
      b.classList.toggle("dark:bg-yellow-900/50", on)
    })

    if (this.showOnlyFavorites) {
      setOn(true)
      if (this.favorites.length === 0) {
        this.showToast("お気に入りがありません")
        this.showOnlyFavorites = false
        setOn(false)
        return
      }
      // Dim non-favorites
      this.node?.transition().duration(300)
        .attr("opacity", n => this.favorites.includes(n.id) ? 1 : 0.1)
      this.link?.transition().duration(300)
        .attr("opacity", 0.1)
      this.showToast(`${this.favorites.length}件のお気に入りを表示`)
    } else {
      setOn(false)
      // Show all
      this.node?.transition().duration(300).attr("opacity", 1)
      this.link?.transition().duration(300).attr("opacity", 0.4)
    }
  }

  updateFavoriteUI(topicId) {
    const btn = document.getElementById("favorite-btn")
    if (!btn) return
    const isFav = this.favorites.includes(topicId)
    btn.innerHTML = isFav
      ? '<svg class="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>'
      : '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>'
  }

  async refreshNews() {
    const btn = document.getElementById("refresh-news-btn")
    const icon = document.getElementById("refresh-icon")
    if (!btn || btn.disabled) return

    btn.disabled = true
    icon.classList.add("animate-spin")
    this.showToast("ニュースを更新中...")

    try {
      const data = await this.postRefresh()
      if (!data) throw new Error("更新エラー")

      if (!data.success) {
        this.showToast("更新に失敗しました: " + (data.error || "不明なエラー"))
      } else if (data.ingested) {
        // 管理者。配信元まで取りに行った結果がそのまま返る
        if (data.created > 0) {
          this.showToast(`${data.created}件の新しい記事を取得しました`)
          setTimeout(() => { this.loadGraph() }, 500)
        } else {
          this.showToast("新しい記事はありませんでした")
        }
      } else {
        // 読み手。取り込みは起こさず、サーバーが持っている最新をそのまま描き直す
        // （配信元の巡回は .github/workflows/news.yml が6時間ごとに回している）
        const changed = this.lastLatestAt && data.latest_at && data.latest_at !== this.lastLatestAt
        this.lastLatestAt = data.latest_at ?? this.lastLatestAt
        this.showToast(changed ? "新しいニュースが届きました" : "最新の状態です")
        setTimeout(() => { this.loadGraph() }, 300)
      }
    } catch (error) {
      this.showToast("更新に失敗しました: " + error.message)
    } finally {
      btn.disabled = false
      icon.classList.remove("animate-spin")
    }
  }

  showToast(message) {
    // Remove existing toast
    document.getElementById("toast")?.remove()

    const toast = document.createElement("div")
    toast.id = "toast"
    // モバイルではボトムナビの上、PCでは従来通り
    const isMobile = window.innerWidth < 640
    toast.className = `fixed ${isMobile ? 'bottom-24' : 'bottom-20'} left-1/2 -translate-x-1/2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-4 py-2 rounded-lg shadow-lg text-sm z-[100] transition-opacity duration-300`
    toast.textContent = message
    document.body.appendChild(toast)

    setTimeout(() => {
      toast.classList.add("opacity-0")
      setTimeout(() => toast.remove(), 300)
    }, 2500)
  }

  copyShareUrl() {
    const url = window.location.href
    if (!navigator.clipboard) {
      prompt("URLをコピーしてください:", url)
      return
    }
    navigator.clipboard.writeText(url).then(() => {
      const btn = document.getElementById("share-btn")
      const originalText = btn.innerHTML
      btn.innerHTML = '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg> コピーしました!'
      btn.classList.add("bg-green-100", "dark:bg-green-900", "text-green-700", "dark:text-green-300")
      setTimeout(() => {
        btn.innerHTML = originalText
        btn.classList.remove("bg-green-100", "dark:bg-green-900", "text-green-700", "dark:text-green-300")
      }, 2000)
    }).catch(() => {
      prompt("URLをコピーしてください:", url)
    })
  }

  // 記事のパネルを閉じるだけ。**絞り込みは解かない。**
  //
  // SP ではパネルが画面を丸ごと覆う（実測 393x798 = 全面）ので、
  // 閉じた時に絞り込みまで解いてしまうと、絞られたグラフを一度も見られない。
  // 「閉じる = 記事を読み終えた」であって「絞り込みをやめる」ではない。
  closePanel() {
    document.getElementById("article-panel")?.classList.add("translate-y-full", "sm:translate-x-full")
    this.setGraphHalf(false)
    if (this.selectedNode) this.showFilterChip(this.selectedNode)
  }

  // SP のシートは「半分」と「全体」の2段。ハンドルで切り替える。
  // 半分の時にトレンドの図まで出すと、記事が2件も見えない高さになる。
  setSheetExpanded(on) {
    const panel = document.getElementById("article-panel")
    if (!panel) return
    this.sheetExpanded = on
    panel.classList.toggle("h-1/2", !on)
    panel.classList.toggle("h-full", on)
    document.getElementById("sheet-handle")?.setAttribute("aria-expanded", on ? "true" : "false")
    this.applyTrendVisibility()
  }

  // トレンドの図を出すのは「PC」か「SP で全体まで開いた時」だけ
  applyTrendVisibility() {
    const trend = document.getElementById("panel-trend")
    if (!trend) return
    const wide = window.innerWidth >= MOBILE_BREAKPOINT
    trend.classList.toggle("hidden", !(wide || this.sheetExpanded))
  }

  // 「このキーワードは使えるか」の投票。**お気に入り（読みたいか）とは別の軸**で、
  // 切れた抽出や、関係ない記事を拾う語を見つけて消すための材料。
  // 仕様: docs/design/news-topic-quality.md
  setQualityVote(topic, loggedIn) {
    const box = document.getElementById("quality-vote")
    if (!box) return
    box.classList.toggle("hidden", !loggedIn)
    box.classList.toggle("flex", !!loggedIn)
    if (!loggedIn) return
    box.dataset.topicId = topic.id
    box.dataset.verdict = topic.quality || ""
    this.paintQualityVote()
  }

  paintQualityVote() {
    const box = document.getElementById("quality-vote")
    if (!box) return
    const v = box.dataset.verdict
    const paint = (id, on, onClass) => {
      const b = document.getElementById(id)
      if (!b) return
      b.setAttribute("aria-pressed", on ? "true" : "false")
      b.classList.toggle(onClass, on)
      b.classList.toggle("text-white", on)
      b.classList.toggle("text-gray-400", !on)
    }
    paint("quality-good", v === "good", "bg-emerald-600")
    paint("quality-bad", v === "bad", "bg-rose-600")
  }

  async voteQuality(verdict) {
    const box = document.getElementById("quality-vote")
    if (!box || box.dataset.busy === "1" || !box.dataset.topicId) return
    box.dataset.busy = "1"
    const clearing = box.dataset.verdict === verdict
    try {
      const json = await this.apiJson(`/news/topic_quality/${box.dataset.topicId}`,
                                      clearing ? "DELETE" : "PUT",
                                      clearing ? null : { verdict })
      if (!json || json.ok === false) return
      box.dataset.verdict = json.verdict || ""
      this.paintQualityVote()
      this.showToast(
        json.verdict === "bad"  ? "おかしいキーワードとして記録しました" :
        json.verdict === "good" ? "使えるキーワードとして記録しました" : "取り消しました")
    } catch (e) {
      console.error("quality vote failed", e)
    } finally {
      box.dataset.busy = "0"
    }
  }

  // --- 複数選択 ---------------------------------------------------------
  // 長押しで入り、以降のタップは足し引き。2つ以上そろったら、その**全部**に
  // 出てくる記事だけを出す。「どれか」ではなく「全部」なのが肝で、
  // 2つ重ねると話が一気に絞れる。仕様: docs/design/news-multi-select.md

  multiOpacityFor(id) {
    if (this.multiIds.has(id)) return 1
    const ids = [...this.multiIds]
    if (ids.length === 0) return 1
    const hits = ids.filter(sid => (this.adjacencyMap?.get(sid) || new Set()).has(id)).length
    if (hits === ids.length) return 1                 // 選んだ全部と繋がっている
    return hits > 0 ? SECOND_DEGREE_OPACITY : 0       // どれかと繋がっている / それ以外
  }

  enterMultiSelect(d) {
    if (!this.multiSelect) {
      this.multiSelect = true
      this.multiIds = new Set()
      // 既に1つ開いていたなら、それを1つ目として引き継ぐ（押し直させない）
      if (this.selectedNode) this.multiIds.add(this.selectedNode.id)
      this.showToast("複数選択モード。タップで足し引きできます")
    }
    this.multiIds.add(d.id)
    this.afterMultiChange()
  }

  toggleMultiSelect(d) {
    if (this.multiIds.has(d.id)) this.multiIds.delete(d.id)
    else this.multiIds.add(d.id)
    if (this.multiIds.size === 0) return this.exitMultiSelect()
    this.afterMultiChange()
  }

  exitMultiSelect() {
    this.multiSelect = false
    this.multiIds = new Set()
    this.clearSelection()
  }

  afterMultiChange() {
    this.applyNeighborTiers(300)
    this.showMultiChip()
    this.setGraphHalf(true)
    if (this.multiIds.size >= 2) this.showIntersectionPanel()
    else this.showToast("もう1つ選ぶと、両方に出てくる記事が出ます")
  }

  selectedNames() {
    return [...this.multiIds]
      .map(id => this.graphData?.nodes?.find(n => n.id === id)?.name)
      .filter(Boolean)
  }

  showMultiChip() {
    const chip = document.getElementById("filter-chip")
    if (!chip) return
    const label = chip.querySelector("span")
    if (label) label.textContent = `${this.multiIds.size}つのキーワードで絞り込み中`
    const name = document.getElementById("filter-chip-name")
    if (name) name.textContent = this.selectedNames().join(" ＋ ")
    chip.classList.remove("hidden")
  }

  async showIntersectionPanel() {
    const panel = document.getElementById("article-panel")
    const title = document.getElementById("panel-title")
    const content = document.getElementById("panel-content")
    if (!panel || !title || !content) return

    panel.classList.remove("translate-y-full", "sm:translate-x-full")
    this.setSheetExpanded(false)
    // キーワード1つに対する操作なので、複数選んでいる間は出さない
    document.getElementById("quality-vote")?.classList.add("hidden")
    document.getElementById("panel-footer")?.classList.add("hidden")

    const ids = [...this.multiIds]
    title.textContent = this.selectedNames().join(" ＋ ")
    content.innerHTML = '<div class="text-center py-8 text-sm text-gray-500">読み込み中...</div>'

    try {
      const res = await fetch(`/news/topics/intersection.json?ids=${ids.join(",")}`)
      const data = await res.json()
      title.textContent = data.topics.map(t => t.name).join(" ＋ ")
      content.innerHTML = this.intersectionHtml(data)
      content.querySelectorAll(".article-link[data-demo='true']").forEach(link => {
        link.addEventListener("click", e => {
          e.preventDefault()
          this.showToast("デモデータのため、実際の記事ページはありません")
        })
      })
    } catch (e) {
      console.error("intersection failed", e)
      content.innerHTML = '<div class="text-center py-8 text-sm text-gray-500">読み込みに失敗しました</div>'
    }
  }

  intersectionHtml(data) {
    const names = data.topics.map(t => t.name).join("」と「")
    if (!data.articles.length) {
      return `
        <div class="text-center py-10 px-4">
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-1">
            「${names}」の両方に出てくる記事はありません
          </p>
          <p class="text-xs text-gray-400">どれか1つを外すか、別の組み合わせを試してください</p>
        </div>`
    }
    const head = `
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
        「${names}」の<strong class="text-gray-900 dark:text-white">両方</strong>に出てくる記事
        <span class="tabular-nums">${data.count}</span>件
      </p>`
    const list = data.articles.map(a => {
      const isDemo = a.url.includes("example.com")
      return `
        <a href="${isDemo ? "#" : `/news/articles/${a.id}`}"
           class="article-link block border-l-4 border-blue-500 bg-gray-50 dark:bg-gray-800 p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition mb-3"
           data-demo="${isDemo}">
          <h4 class="font-bold text-gray-900 dark:text-white text-sm leading-snug mb-1">${a.title}</h4>
          <div class="text-xs text-gray-500">${a.media_name} · ${a.published_at || ""}</div>
          ${a.summary ? `<p class="text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">${a.summary}</p>` : ""}
        </a>`
    }).join("")
    return head + list
  }

  isPanelOpen() {
    const panel = document.getElementById("article-panel")
    return !!panel && !panel.classList.contains("translate-y-full")
  }

  // SP で記事のシートが開いている間、グラフを上半分に縮める。
  // シートが全面を覆っていると、絞り込んだ結果を一度も見られない。
  // PC は sm:h-full が効くので見た目は変わらない。
  setGraphHalf(on) {
    const container = document.getElementById("graph-container")
    if (!container) return
    if (on) {
      container.classList.remove("h-full")
      container.classList.add("h-1/2", "sm:h-full")
    } else {
      container.classList.remove("h-1/2", "sm:h-full")
      container.classList.add("h-full")
    }
    // 箱が変わったので、d3 が持っている画面の大きさも入れ替える
    this.svgWidth = container.clientWidth
    this.svgHeight = container.clientHeight
    d3.select("#newspaper-graph").attr("width", this.svgWidth).attr("height", this.svgHeight)
    this.redrawWrapped?.()
  }

  // 絞り込みを解いて全部を出し直す。パネルを閉じるのとは別の操作。
  clearSelection() {
    this.hideFilterChip()
    document.getElementById("article-panel")?.classList.add("translate-y-full", "sm:translate-x-full")
    this.setGraphHalf(false)

    const url = new URL(window.location)
    url.searchParams.delete("topic")
    window.history.replaceState({}, "", url)

    if (this.node && this.link) {
      this.node.transition().duration(300)
        .attr("opacity", 1)
        .attr("pointer-events", "all")
      this.node.select(".card-bg").transition().duration(300).attr("fill", "#fff").attr("stroke-width", 2)
      // 視認性向上のためコントラストを維持
      this.link.transition().duration(300)
        .attr("stroke", d => {
          const sId = typeof d.source === "object" ? d.source.id : d.source
          const tId = typeof d.target === "object" ? d.target.id : d.target
          return this.categoryMap?.get(sId) !== this.categoryMap?.get(tId) ? "#9333ea" : "#9ca3af"
        })
        .attr("stroke-opacity", 0.5)
        .attr("opacity", 1)
        .attr("stroke-width", d => Math.max(1.5, Math.sqrt(d.weight) * 1.5))
    }

    this.selectedNode = null
    this.connectedNodes.clear()
    this.secondDegreeNodes?.clear()
    this.simulation?.alpha(0.3).restart()
  }

  // 何で絞られているのかを画面に出す。出さないと、パネルを閉じた後の
  // 「一部しか出ていないグラフ」が壊れているようにしか見えない
  showFilterChip(node) {
    const chip = document.getElementById("filter-chip")
    if (!chip) return
    const label = chip.querySelector("span")
    if (label) label.textContent = "つながりで絞り込み中"
    const name = document.getElementById("filter-chip-name")
    if (name) name.textContent = node.name
    chip.classList.remove("hidden")
  }

  hideFilterChip() {
    document.getElementById("filter-chip")?.classList.add("hidden")
  }

  dragStarted(event, d) {
    if (!event.active) this.simulation.alphaTarget(0.3).restart()
    d.fx = d.x
    d.fy = d.y
    // 長押しの判定は d3.drag に相乗りさせる。別に pointerdown を張ると、
    // drag が押しを持っていくので二重に拾ってしまう
    this.longPressFired = false
    clearTimeout(this.longPressTimer)
    this.longPressTimer = setTimeout(() => {
      this.longPressFired = true
      this.enterMultiSelect(d)
    }, LONG_PRESS_MS)
  }

  dragged(event, d) {
    // 指が動いたらそれは「掴んで動かす」なので、長押しは取り消す
    clearTimeout(this.longPressTimer)
    d.fx = event.x
    d.fy = event.y
  }

  dragEnded(event, d) {
    clearTimeout(this.longPressTimer)
    if (!event.active) this.simulation.alphaTarget(0)
    d.fx = null
    d.fy = null
  }

  setupPlayback() {
    const playbackBtn = document.getElementById("playback-btn")
    const playbackPanel = document.getElementById("playback-panel")
    const playbackClose = document.getElementById("playback-close")
    const playbackPlay = document.getElementById("playback-play")
    const playbackSlider = document.getElementById("playback-slider")

    if (!playbackBtn || !playbackPanel) return

    this.playbackData = null
    this.playbackIndex = 0
    this.isPlaying = false

    playbackBtn.addEventListener("click", async () => {
      playbackPanel.classList.toggle("hidden")
      if (!playbackPanel.classList.contains("hidden") && !this.playbackData) {
        await this.loadPlaybackData()
      }
    })

    playbackClose?.addEventListener("click", () => {
      playbackPanel.classList.add("hidden")
      this.stopPlayback()
    })

    playbackPlay?.addEventListener("click", () => {
      if (this.isPlaying) {
        this.stopPlayback()
      } else {
        this.startPlayback()
      }
    })

    playbackSlider?.addEventListener("input", (e) => {
      this.playbackIndex = parseInt(e.target.value)
      this.showPlaybackFrame(this.playbackIndex)
    })

    document.getElementById("playback-prev")?.addEventListener("click", () => {
      if (this.playbackData && this.playbackIndex > 0) {
        this.playbackIndex--
        this.showPlaybackFrame(this.playbackIndex)
      }
    })

    document.getElementById("playback-next")?.addEventListener("click", () => {
      if (this.playbackData && this.playbackIndex < this.playbackData.snapshots.length - 1) {
        this.playbackIndex++
        this.showPlaybackFrame(this.playbackIndex)
      }
    })
  }

  async loadPlaybackData() {
    try {
      const url = new URL(window.location.origin + "/news/topics/playback_data")
      url.searchParams.set("days", this.daysValue)
      const response = await fetch(url)
      if (!response.ok) throw new Error(response.status)
      this.playbackData = await response.json()
      
      const slider = document.getElementById("playback-slider")
      if (slider && this.playbackData.snapshots.length > 0) {
        slider.max = this.playbackData.snapshots.length - 1
        slider.value = 0
        this.playbackIndex = 0
        
        const startLabel = document.getElementById("playback-start-date")
        const endLabel = document.getElementById("playback-end-date")
        if (startLabel) startLabel.textContent = this.playbackData.snapshots[0].date_label
        if (endLabel) endLabel.textContent = this.playbackData.snapshots[this.playbackData.snapshots.length - 1].date_label
        
        this.showPlaybackFrame(0)
      }
    } catch (e) {
      console.error("Failed to load playback data:", e)
    }
  }

  startPlayback() {
    if (!this.playbackData) return
    this.isPlaying = true
    const icon = document.getElementById("playback-play-icon")
    if (icon) icon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>'
    
    this.playbackInterval = setInterval(() => {
      if (this.playbackIndex < this.playbackData.snapshots.length - 1) {
        this.playbackIndex++
        this.showPlaybackFrame(this.playbackIndex)
      } else {
        this.stopPlayback()
      }
    }, 1500)
  }

  stopPlayback() {
    this.isPlaying = false
    clearInterval(this.playbackInterval)
    const icon = document.getElementById("playback-play-icon")
    if (icon) icon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>'
  }

  showPlaybackFrame(index) {
    if (!this.playbackData || !this.playbackData.snapshots[index]) return
    const snapshot = this.playbackData.snapshots[index]
    const dateLabel = document.getElementById("playback-date")
    const slider = document.getElementById("playback-slider")
    if (dateLabel) dateLabel.textContent = snapshot.date_label
    if (slider) slider.value = index
    this.renderGraph({ nodes: snapshot.nodes, links: snapshot.links })
  }

  setupMediaFilter() {
    const filterBtn = document.getElementById("media-filter-btn")
    const filterDropdown = document.getElementById("media-filter-dropdown")
    const filterReset = document.getElementById("media-filter-reset")

    if (!filterBtn || !filterDropdown) return

    this.selectedMedia = new Set()

    filterBtn.addEventListener("click", (e) => {
      e.stopPropagation()
      filterDropdown.classList.toggle("hidden")
    })

    document.addEventListener("click", (e) => {
      if (!filterDropdown.contains(e.target) && e.target !== filterBtn) {
        filterDropdown.classList.add("hidden")
      }
    })

    filterReset?.addEventListener("click", () => {
      this.selectedMedia.clear()
      this.updateMediaFilterUI()
      this.applyMediaFilter()
    })
  }

  populateMediaFilter(mediaList) {
    const filterList = document.getElementById("media-filter-list")
    if (!filterList || !mediaList) return

    filterList.innerHTML = mediaList.map(media => `
      <label class="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
        <input type="checkbox" class="media-checkbox rounded border-gray-300 text-blue-500" data-media="${media}" ${this.selectedMedia.has(media) ? 'checked' : ''}>
        <span class="text-sm text-gray-700 dark:text-gray-300 truncate">${media}</span>
      </label>
    `).join("")

    filterList.querySelectorAll(".media-checkbox").forEach(checkbox => {
      checkbox.addEventListener("change", (e) => {
        const media = e.target.dataset.media
        if (e.target.checked) {
          this.selectedMedia.add(media)
        } else {
          this.selectedMedia.delete(media)
        }
        this.updateMediaFilterUI()
        this.applyMediaFilter()
      })
    })
  }

  updateMediaFilterUI() {
    const countBadge = document.getElementById("media-filter-count")
    if (countBadge) {
      if (this.selectedMedia.size > 0) {
        countBadge.textContent = this.selectedMedia.size
        countBadge.classList.remove("hidden")
      } else {
        countBadge.classList.add("hidden")
      }
    }
  }

  applyMediaFilter() {
    if (!this.node || !this.graphData) return
    const selected = this.selectedMedia

    if (selected.size === 0) {
      this.node.style("opacity", 1)
      this.link.style("opacity", 0.4)
      this.label?.style("opacity", 1)
      this.filteredNodeIds = null
      return
    }

    const matchIds = new Set()
    this.graphData.nodes.forEach(n => {
      if (n.media_sources?.some(m => selected.has(m))) matchIds.add(n.id)
    })
    this.filteredNodeIds = matchIds

    this.node.style("opacity", d => matchIds.has(d.id) ? 1 : 0.08)
    this.label?.style("opacity", d => matchIds.has(d.id) ? 1 : 0.08)
    this.link.style("opacity", d => {
      const sId = typeof d.source === "object" ? d.source.id : d.source
      const tId = typeof d.target === "object" ? d.target.id : d.target
      return matchIds.has(sId) && matchIds.has(tId) ? 0.4 : 0.03
    })

    this.showToast(`${selected.size}件のメディアで絞り込み: ${matchIds.size}トピック表示`)
  }

  // ======== Mobile UI Setup ========
  setupMobileUI() {
    // Mobile Search
    const mobileSearchBtn = document.getElementById("mobile-search-btn")
    const mobileSearchModal = document.getElementById("mobile-search-modal")
    const mobileSearchClose = document.getElementById("mobile-search-close")
    const mobileSearchInput = document.getElementById("mobile-topic-search")
    const mobileSearchResults = document.getElementById("mobile-search-results")

    mobileSearchBtn?.addEventListener("click", () => {
      mobileSearchModal?.classList.remove("hidden")
      setTimeout(() => mobileSearchInput?.focus(), 100)
    })

    mobileSearchClose?.addEventListener("click", () => {
      mobileSearchModal?.classList.add("hidden")
    })

    mobileSearchInput?.addEventListener("input", () => {
      const query = mobileSearchInput.value.trim().toLowerCase()
      if (query.length < 1) {
        mobileSearchResults.innerHTML = '<div class="text-sm text-gray-400 text-center py-8">トピック名を入力してください</div>'
        return
      }
      this.performMobileSearch(query, mobileSearchResults, mobileSearchModal)
    })

    // Mobile Discover
    document.getElementById("mobile-discover-btn")?.addEventListener("click", () => {
      this.discoverRandom()
      this.closeMobileModals()
    })

    // Mobile Legend Modal
    const mobileLegendBtn = document.getElementById("mobile-legend-btn")
    const mobileLegendModal = document.getElementById("mobile-legend-modal")
    mobileLegendBtn?.addEventListener("click", () => {
      this.toggleMobileModal(mobileLegendModal)
    })

    // Mobile Stats Modal
    const mobileStatsBtn = document.getElementById("mobile-stats-btn")
    const mobileStatsModal = document.getElementById("mobile-stats-modal")
    mobileStatsBtn?.addEventListener("click", () => {
      this.toggleMobileModal(mobileStatsModal)
      this.populateMobileStats()
    })

    // Mobile Menu Modal
    const mobileMenuBtn = document.getElementById("mobile-menu-btn")
    const mobileMenuModal = document.getElementById("mobile-menu-modal")
    mobileMenuBtn?.addEventListener("click", () => {
      this.toggleMobileModal(mobileMenuModal)
    })

    // シートは下スワイプでも閉じられるが、それだけだと気づけないので閉じるボタンも置く
    document.getElementById("mobile-menu-close")?.addEventListener("click", () => {
      this.closeMobileModals()
    })

    // Mobile Menu Actions
    document.getElementById("mobile-playback-btn")?.addEventListener("click", () => {
      this.closeMobileModals()
      document.getElementById("playback-btn")?.click()
    })

    document.getElementById("mobile-filter-btn")?.addEventListener("click", () => {
      this.closeMobileModals()
      document.getElementById("media-filter-btn")?.click()
    })

    document.getElementById("mobile-refresh-btn")?.addEventListener("click", () => {
      this.closeMobileModals()
      this.refreshNews()
    })

    document.getElementById("mobile-darkmode-btn")?.addEventListener("click", () => {
      this.closeMobileModals()
      document.querySelector('[data-action="click->dark-mode#toggle"]')?.click()
    })

    document.getElementById("mobile-fullscreen-btn")?.addEventListener("click", () => {
      this.closeMobileModals()
      this.toggleFullscreen()
    })

    // Mobile Stats Tabs
    document.querySelectorAll(".mobile-stats-tab").forEach(tab => {
      tab.addEventListener("click", () => {
        document.querySelectorAll(".mobile-stats-tab").forEach(t => {
          t.classList.remove("text-gray-700", "dark:text-gray-300", "border-blue-500")
          t.classList.add("text-gray-400", "dark:text-gray-500", "border-transparent")
        })
        tab.classList.remove("text-gray-400", "dark:text-gray-500", "border-transparent")
        tab.classList.add("text-gray-700", "dark:text-gray-300", "border-blue-500")
        this.populateMobileStats(tab.dataset.mobileStatsTab)
      })
    })

    // Close modals on backdrop click
    document.querySelectorAll("#mobile-legend-modal, #mobile-stats-modal, #mobile-menu-modal").forEach(modal => {
      modal?.addEventListener("click", (e) => {
        if (e.target === modal) this.closeMobileModals()
      })
    })

    // Swipe down to close modals
    let touchStartY = 0
    document.querySelectorAll("#mobile-legend-modal, #mobile-stats-modal, #mobile-menu-modal").forEach(modal => {
      modal?.addEventListener("touchstart", (e) => {
        touchStartY = e.touches[0].clientY
      })
      modal?.addEventListener("touchmove", (e) => {
        const touchY = e.touches[0].clientY
        if (touchY - touchStartY > 80) {
          this.closeMobileModals()
        }
      })
    })
  }

  performMobileSearch(query, container, modal) {
    if (!this.graphData?.nodes) return

    const results = this.graphData.nodes.filter(n =>
      n.name.toLowerCase().includes(query)
    ).slice(0, 10)

    if (results.length === 0) {
      container.innerHTML = '<div class="text-sm text-gray-400 text-center py-8">見つかりませんでした</div>'
      return
    }

    container.innerHTML = results.map(n => {
      return `
        <button class="mobile-search-result w-full text-left p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-between gap-2 transition mb-2" data-topic-id="${n.id}">
          <span class="text-base text-gray-900 dark:text-white">${this.highlightMatch(n.name, query)}</span>
          <span class="flex-shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded text-sm bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"><span class="w-2 h-2 rounded-full flex-shrink-0" style="background-color: ${this.catHex(n.category)}"></span>${n.size}件</span>
        </button>
      `
    }).join("")

    container.querySelectorAll(".mobile-search-result").forEach(btn => {
      btn.addEventListener("click", () => {
        const topic = this.graphData.nodes.find(n => n.id === parseInt(btn.dataset.topicId))
        if (topic) {
          modal?.classList.add("hidden")
          this.selectNode(topic, this.node, this.link, this.colorScale, this.cardWidth, this.cardHeight)
          this.panToNode(topic)
        }
      })
    })
  }

  toggleMobileModal(modal) {
    if (!modal) return
    const isHidden = modal.classList.contains("translate-y-full")
    this.closeMobileModals()
    if (isHidden) {
      modal.classList.remove("translate-y-full")
    }
  }

  closeMobileModals() {
    document.querySelectorAll("#mobile-legend-modal, #mobile-stats-modal, #mobile-menu-modal").forEach(modal => {
      modal?.classList.add("translate-y-full")
    })
    document.getElementById("mobile-search-modal")?.classList.add("hidden")
  }

  populateMobileStats(tab = "trending") {
    const content = document.getElementById("mobile-stats-content")
    if (!content) return

    if (tab === "trending") {
      const trendingList = document.getElementById("trending-list")
      if (trendingList) {
        content.innerHTML = trendingList.innerHTML
      } else {
        content.innerHTML = '<div class="text-sm text-gray-400 text-center py-4">データなし</div>'
      }
    } else if (tab === "correlation") {
      const correlationList = document.getElementById("correlation-list")
      if (correlationList) {
        content.innerHTML = correlationList.innerHTML
      }
    } else if (tab === "media") {
      const mediaList = document.getElementById("media-list")
      if (mediaList) {
        content.innerHTML = mediaList.innerHTML
      }
    }

    // Add click handlers for mobile stats items
    content.querySelectorAll("[data-topic-id]").forEach(btn => {
      btn.addEventListener("click", () => {
        const topicId = parseInt(btn.dataset.topicId)
        const topic = this.graphData?.nodes?.find(n => n.id === topicId)
        if (topic) {
          this.closeMobileModals()
          this.selectNode(topic, this.node, this.link, this.colorScale, this.cardWidth, this.cardHeight)
          this.panToNode(topic)
        }
      })
    })
  }

  // ======== Legend Toggle ========
  setupLegendToggle() {
    const toggleBtn = document.getElementById("toggle-legend")
    const content = document.getElementById("legend-content")
    const chevron = document.getElementById("legend-chevron")

    toggleBtn?.addEventListener("click", () => {
      content?.classList.toggle("hidden")
      chevron?.classList.toggle("rotate-180")
    })
  }

  // ======== Trending Hover Highlight ========
  setupTrendingHover() {
    document.querySelectorAll(".trending-topic, .correlation-topic").forEach(btn => {
      btn.addEventListener("mouseenter", () => {
        const topicId = parseInt(btn.dataset.topicId)
        this.highlightNodeOnGraph(topicId)
      })
      btn.addEventListener("mouseleave", () => {
        this.clearNodeHighlight()
      })
    })
  }

  highlightNodeOnGraph(topicId) {
    if (!this.node) return
    this.node.transition().duration(200)
      .attr("opacity", n => n.id === topicId ? 1 : 0.3)
    this.node.filter(n => n.id === topicId)
      .select(".card-bg")
      .transition().duration(200)
      .attr("stroke-width", 4)
  }

  clearNodeHighlight() {
    if (!this.node || this.selectedNode) return
    this.node.transition().duration(200).attr("opacity", 1)
    this.node.select(".card-bg").transition().duration(200).attr("stroke-width", 2)
  }

  setupPanelSwipe() {
    const panel = document.getElementById("article-panel")
    if (!panel) return

    let startY = 0
    let currentY = 0
    let swiping = false

    panel.addEventListener("touchstart", (e) => {
      if (panel.scrollTop > 0) return
      startY = e.touches[0].clientY
      // **ここで currentY も入れ直す。** 入れないと、指を動かさずに軽く触れただけの時に
      // 前のなぞりの値が残ったままで dy が計算され、触っただけでシートが開いたり
      // 閉じたりする（取っ手を押すと記事へ飛んでしまっていた）
      currentY = startY
      swiping = true
    }, { passive: true })

    panel.addEventListener("touchmove", (e) => {
      if (!swiping) return
      currentY = e.touches[0].clientY
      const dy = currentY - startY
      if (dy > 0) {
        panel.style.transform = `translateY(${dy}px)`
        panel.style.transition = "none"
      }
    }, { passive: true })

    panel.addEventListener("touchend", () => {
      if (!swiping) return
      swiping = false
      const dy = currentY - startY
      panel.style.transition = "transform 0.3s ease"
      if (dy > 100) {
        // 全体まで開いていたら、いきなり閉じずに半分へ戻す
        if (this.sheetExpanded) this.setSheetExpanded(false)
        else this.closePanel()
      } else if (dy < -60) {
        this.setSheetExpanded(true)   // 上へ払ったら全体まで開く
      }
      panel.style.transform = ""
    })
  }
}
